import type { Dictionary } from './types';

// Thai copy for a Thai-developer audience. Technical terms customers already
// know in English stay in English inside the Thai sentence — API key,
// token, streaming, region, endpoint, prefix — per the product decision:
// translating those into formal Thai reads as stilted and makes the product
// harder to use, not easier.
export const th: Dictionary = {
  common: {
    dashboard: 'ภาพรวม',
    apiKeys: 'API keys',
    usage: 'การใช้งาน',
    reports: 'รายงาน',
    models: 'โมเดล',
    topUp: 'กระเป๋าเครดิต',
    team: 'ทีม',
    playground: 'ทดลองใช้งาน',
    guide: 'คู่มือ',
    signOut: 'ออกจากระบบ',
    editProfile: 'แก้ไขโปรไฟล์',
    personal: 'Personal',
    backToWebsite: 'กลับสู่เว็บไซต์ IC Lueur Ai',
    skipToContent: 'ข้ามไปยังเนื้อหา',
    primaryNav: 'เมนูหลัก',
    cancel: 'ยกเลิก',
    close: 'ปิด',
    working: 'กำลังดำเนินการ…',
    never: 'ไม่เคย',
    previous: 'ก่อนหน้า',
    next: 'ถัดไป',
    loadMore: 'โหลดเพิ่มเติม',
    loading: 'กำลังโหลด…',
    clear: 'ล้างค่า',
    // Matches the compact "Page N of M" form used by OffsetPager. Slightly
    // longer than "หน้า 1/6" but still fits between prev/next on 375px.
    pageOf: (page, totalPages) => `หน้า ${page} จาก ${totalPages}`,
    itemCount: (n) => `${n.toLocaleString()} รายการ`,
    showingSoFar: (n) => `แสดง ${n.toLocaleString()} รายการ`,
    pageNumber: (page) => `หน้า ${page}`,
    rowsPerPage: 'แสดงต่อหน้า',
    goToPage: (page) => `ไปหน้า ${page}`,
    otpDidntGetCode: 'ไม่ได้รับรหัส?',
    otpResendLink: 'คลิกเพื่อส่งอีกครั้ง',
    otpDigitLabel: (digit, total) => `หลักที่ ${digit} จาก ${total}`,
    orgSwitcherLabel: 'องค์กร',
    datePicker: {
      today: 'วันนี้',
      previousMonth: 'เดือนก่อน',
      nextMonth: 'เดือนถัดไป',
      weekdays: ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'],
      months: [
        'มกราคม',
        'กุมภาพันธ์',
        'มีนาคม',
        'เมษายน',
        'พฤษภาคม',
        'มิถุนายน',
        'กรกฎาคม',
        'สิงหาคม',
        'กันยายน',
        'ตุลาคม',
        'พฤศจิกายน',
        'ธันวาคม',
      ],
    },
  },
  notifications: {
    bellLabel: 'การแจ้งเตือน',
    panelHeading: 'การแจ้งเตือน',
    empty: 'ยังไม่มีการแจ้งเตือน',
    loadError: 'ไม่สามารถโหลดการแจ้งเตือนได้',
    viewAllLink: 'ดูการแจ้งเตือนทั้งหมด',
    historyHeading: 'การแจ้งเตือน',
    historyDescription: 'ทุกการแจ้งเตือนที่ส่งถึงคุณ เรียงจากล่าสุด',
    filterLabel: 'กรองตามประเภท',
    filterAll: 'ทุกประเภท',
    typeLabel: (type, isB2B) => {
      switch (type) {
        case 'key_suspended_abuse':
          return 'คีย์ API ถูกระงับ';
        case 'low_balance':
          return 'ยอดเงินคงเหลือต่ำ';
        case 'free_quota_fallback_billing':
          return 'โควต้าฟรีหมด เรียกเก็บเงินแล้ว';
        case 'topup_requested':
          return 'มีคำขอเติมเงิน';
        case 'added_to_org':
          return isB2B ? 'ถูกเพิ่มเข้าองค์กร' : 'ถูกเพิ่มเข้าทีม';
        default:
          return type;
      }
    },
    actionGoToPage: 'ดูรายละเอียด',
    actionContactSupport: 'ติดต่อฝ่ายสนับสนุน',
  },
  themeToggle: {
    toLight: 'สลับเป็นธีมสว่าง',
    toDark: 'สลับเป็นธีมมืด',
  },
  languageSwitcher: {
    switchToEnglish: 'สลับเป็นภาษาอังกฤษ',
    switchToThai: 'สลับเป็นภาษาไทย',
    english: 'EN',
    thai: 'TH',
  },
  dashboard: {
    subtitle: 'ยอดเครดิต กิจกรรม และทางลัดที่ใช้ง่าย',
    greetingMorning: (name) => `สวัสดีตอนเช้า ${name}`,
    greetingAfternoon: (name) => `สวัสดีตอนบ่าย ${name}`,
    greetingEvening: (name) => `สวัสดีตอนเย็น ${name}`,
    creditBalance: 'ยอดเครดิตคงเหลือ',
    depleted: 'ยอดเครดิตของคุณหมดแล้ว ระบบจะไม่สามารถดำเนินการคำขอ API ได้จนกว่าจะเติมเครดิต',
    unfunded: 'ยังไม่มีเครดิต กรุณาเติมเครดิตเพื่อเริ่มใช้งานบริการ',
    prepaidNote: 'เครดิตแบบเติมเงินล่วงหน้า ใช้เท่าไหร่หักเท่านั้น เครดิตที่เหลือไม่มีวันหมดหรือลดลงเอง',
    topUpCta: 'เติมเครดิต',
    balanceRestricted: (isB2B) =>
      isB2B ? 'เฉพาะ admin และ owner เท่านั้นที่ดูยอดคงเหลือขององค์กรได้' : 'เฉพาะ admin และ owner เท่านั้นที่ดูยอดคงเหลือของบัญชีได้',
    memberCreditSufficient: 'เครดิตพร้อมใช้งาน',
    memberCreditDepleted: 'เครดิตหมด',
    memberCreditUnfunded: 'ยังไม่มีเครดิต',
    memberCreditDepletedContact: 'ติดต่อ admin หรือ owner เพื่อเติมเครดิต',
    memberCreditUnfundedContact: 'แจ้ง admin หรือ owner ให้เติมเครดิตเพื่อเริ่มใช้งาน',
    requestTopUpCta: 'ส่งคำขอเติมเงิน',
    requestTopUpSent: 'ส่งคำขอแล้ว แจ้ง admin เรียบร้อย',
    requestTopUpCooldown: 'เพิ่งส่งคำขอไปแล้ว ลองใหม่ภายหลัง',
    requestTopUpError: 'ส่งคำขอไม่สำเร็จ ลองใหม่อีกครั้ง',
    requestTopUpSendAll: 'ส่งทั้งหมด (admin และ owner)',
    requestTopUpSendSpecific: 'ระบุคนที่ต้องการ',
    requestTopUpPickPlaceholder: 'เลือกคน…',
    requestTopUpSendTo: 'ส่ง',
    recentUsage: 'การใช้งานล่าสุด',
    viewAll: 'ดูทั้งหมด',
    colWhen: 'เวลา',
    colModel: 'โมเดล',
    colIn: 'อินพุต',
    colOut: 'เอาต์พุต',
    colCost: 'ค่าใช้จ่าย (บาท)',
    emptyTitle: 'ยังไม่มีคำขอ',
    emptyBody: 'สร้าง API key แล้วเรียกใช้งานครั้งแรกเพื่อดูข้อมูลที่นี่',
    emptyCta: 'สร้าง API key',
    statusInterrupted: 'ถูกขัดจังหวะ',
    statusFailed: 'ล้มเหลว',
    loadError: 'ไม่สามารถโหลดการใช้งานล่าสุดได้',
    quickLinks: {
      heading: 'ทางลัด',
      keysTitle: 'API keys',
      keysBody: 'สร้างและจัดการคีย์ที่แอปพลิเคชันของคุณใช้เรียก API',
      usageTitle: 'การใช้งาน',
      usageBody: 'ยอดใช้จ่ายตามช่วงเวลา แยกตามวัน โมเดล หรือคีย์',
      modelsTitle: 'โมเดล',
      modelsBody: 'โมเดลทั้งหมดบนแพลตฟอร์ม พร้อมค่าใช้จ่ายในการเรียกใช้',
      topupTitle: 'เติมเงิน',
      topupBody: 'เติมเครดิตล่วงหน้าผ่าน PromptPay',
    },
    activity: {
      heading: 'กิจกรรม',
      loadError: 'ไม่สามารถโหลดกิจกรรมได้',
      loading: 'กำลังโหลดกิจกรรม…',
      emptyTitle: 'ยังไม่มีกิจกรรม',
      emptyBody: 'ยอดใช้จ่ายจะแสดงที่นี่เมื่อคุณเริ่มเรียกใช้ API',
      summary: (total, streakDays) =>
        `กิจกรรมย้อนหลังหนึ่งปี ยอดรวม ${total} สถิติต่อเนื่องยาวสุด ${streakDays} วัน`,
      longestStreak: 'สถิติต่อเนื่องยาวสุด',
      days: (n) => `${n} วัน`,
      avgPerDay: 'เฉลี่ย / วัน',
      avgPerWeek: 'เฉลี่ย / สัปดาห์',
      total: 'รวมทั้งหมด',
      less: 'น้อย',
      more: 'มาก',
      weekdayMon: 'จ',
      weekdayWed: 'พ',
      weekdayFri: 'ศ',
    },
  },
  keys: {
    subtitle: 'คีย์ใช้ยืนยันตัวตนบนแอปพลิเคชันของคุณกับ gateway',
    createHeading: 'สร้างคีย์ใหม่',
    viewSetupGuide: 'ดูคู่มือการตั้งค่า',
    nameLabel: 'ชื่อ',
    namePlaceholder: 'เช่น production-server',
    noteLabel: 'หมายเหตุ',
    notePlaceholder: 'ไม่บังคับ — เช่น ใช้กับ billing-worker',
    creating: 'กำลังสร้าง…',
    createButton: 'สร้างคีย์',
    createError: 'ไม่สามารถสร้างคีย์ได้',
    yourKeys: 'คีย์ของคุณ',
    listError: 'ไม่สามารถโหลดคีย์ของคุณได้',
    colName: 'ชื่อ',
    colPrefix: 'Prefix',
    colRouting: 'Routing',
    colStatus: 'สถานะ',
    colCreated: 'สร้างเมื่อ',
    colLastUsed: 'ใช้งานล่าสุด',
    neverUsed: 'ยังไม่เคยใช้งาน',
    colExpires: 'หมดอายุ',
    colNote: 'หมายเหตุ',
    sortLabel: 'เรียงตาม',
    sortOptions: {
      created_desc: 'ใหม่ล่าสุด',
      expires_asc: 'ใกล้หมดอายุก่อน',
      expires_desc: 'หมดอายุช้าสุดก่อน',
    },
    actionsAria: 'การดำเนินการ',
    // Wording is ours, not the API's — shown read-only per key (list +
    // detail). ลูกค้าไม่ได้เป็นผู้เลือกนโยบายนี้เองแล้ว (ทีมงานตั้งค่าระดับองค์กรแทน)
    // แต่ยังคงแปลไว้เพื่อแสดงผลค่าที่ใช้อยู่.
    routing: {
      strict: 'เข้มงวด (เฉพาะภูมิภาคใกล้เคียง)',
      regional: 'ตามภูมิภาค (ค่าเริ่มต้น)',
      available: 'ทุกภูมิภาค',
    },
    status: {
      active: 'ใช้งานอยู่',
      revoked: 'เพิกถอนแล้ว',
      expired: 'หมดอายุ',
    },
    revoke: 'เพิกถอน',
    detail: 'รายละเอียด',
    detailTitle: (name) => name,
    detailPrefixNote:
      'คีย์ลับแบบเต็มจะแสดงเพียงครั้งเดียวตอนสร้าง ค่า prefix นี้ใช้ระบุคีย์ในรายการเท่านั้น — ไม่สามารถใช้ยืนยันตัวตนได้',
    colRateLimit: 'Rate limit',
    rateLimitUnlimited: 'ไม่ได้ตั้งค่า (ใช้ค่าเริ่มต้นของ gateway)',
    rateLimitPerMinute: (n) => `${n} คำขอ / นาที`,
    edit: 'แก้ไข',
    editTitle: (name) => `จำกัดการใช้งานคีย์ — ${name}`,
    rateLimitLabel: 'จำนวนคำขอต่อนาที',
    rateLimitHelp:
      'ค่านี้จำกัดความถี่ในการเรียกใช้คีย์เท่านั้น ไม่เกี่ยวกับระยะเวลาที่คีย์ใช้งานได้ — ระยะเวลาการใช้งานควบคุมแยกต่างหากที่ "วันหมดอายุ" ด้านล่าง',
    rateLimitUnlimitedLabel: 'ไม่จำกัด (ใช้ค่าเริ่มต้นของ gateway)',
    rateLimitPlaceholder: 'เช่น 60',
    editSave: 'บันทึก',
    editSaving: 'กำลังบันทึก…',
    editError: 'ไม่สามารถแก้ไข rate limit ได้',
    expiresLabel: 'วันหมดอายุ',
    neverExpires: 'ไม่มีวันหมดอายุ',
    expiryPresetLabel: {
      none: 'ไม่มีวันหมดอายุ',
      '1h': '1 ชั่วโมง',
      '1d': '1 วัน',
      '7d': '7 วัน',
      '30d': '30 วัน',
      '90d': '90 วัน',
      '180d': '180 วัน',
      '1y': '1 ปี',
    },
    expiryKeepCurrent: 'คงค่าเดิม',
    expiryCurrentlyExpires: (when) => `ปัจจุบันหมดอายุ ${when}`,
    expiryCurrentlyNever: 'ปัจจุบันไม่มีวันหมดอายุ',
    emptyTitle: 'ยังไม่มี API key',
    emptyBody: 'สร้างคีย์ด้านบนเพื่อเริ่มเรียกใช้ API',
    revokeTitle: 'เพิกถอนคีย์นี้ใช่หรือไม่?',
    // Safety-critical: must be unambiguous that anything currently using
    // this key breaks immediately.
    revokeBodySuffix: (prefix) =>
      ` (${prefix}…) จะหยุดทำงานทันทีสำหรับคำขอใหม่ทั้งหมด ระบบหรือแอปพลิเคชันใดก็ตามที่กำลังใช้คีย์นี้อยู่จะเรียก API ไม่ได้อีกต่อไป ส่วนคำตอบที่กำลัง streaming อยู่ในขณะนี้จะได้รับอนุญาตให้ทำงานจนเสร็จสิ้น`,
    revokeBodyWarning: 'การกระทำนี้ไม่สามารถย้อนกลับได้ คีย์ที่ถูกเพิกถอนแล้วไม่สามารถเปิดใช้งานใหม่ได้อีก — ต้องสร้างคีย์ใหม่แทน',
    revokeConfirmLabel: 'เพิกถอนคีย์',
    revokeError: 'ไม่สามารถเพิกถอนคีย์นี้ได้',
    secretModalTitle: 'บันทึกคีย์นี้ไว้ตอนนี้',
    // Safety-critical: must make clear the secret can never be recovered.
    secretWarning: 'คีย์ลับนี้จะแสดงเพียงครั้งเดียวเท่านั้น และไม่สามารถกู้คืนได้อีกเลยหลังจากนี้',
    secretWarningBody:
      'เราเก็บเฉพาะค่า hash ของคีย์นี้ไว้เท่านั้น หากคุณทำคีย์หาย วิธีแก้ไขเดียวคือเพิกถอนคีย์นี้แล้วสร้างคีย์ใหม่',
    copy: 'คัดลอก',
    copied: 'คัดลอกแล้ว',
    ackLabel: 'ฉันได้คัดลอกคีย์นี้และบันทึกไว้ในที่ปลอดภัยแล้ว',
    secretBaseUrlHint: (baseUrl: string) =>
      `โปรดชี้ Base URL ไปที่ ${baseUrl}`,
    done: 'เสร็จสิ้น',
  },
  models: {
    subtitle: 'โมเดลทั้งหมดบนแพลตฟอร์ม พร้อมค่าใช้จ่ายในการเรียกใช้',
    loadError: 'ไม่สามารถโหลดรายการโมเดลได้',
    searchLabel: 'ค้นหา',
    searchPlaceholder: 'ค้นหาชื่อโมเดล',
    providerLabel: 'ผู้ให้บริการ',
    allProviders: 'ผู้ให้บริการทั้งหมด',
    regionLabel: 'Region',
    allRegions: 'ทุกภูมิภาค',
    // City-name hint only — the code itself (e.g. ap-southeast-7) is always
    // rendered alongside this, unchanged, in both locales. See types.ts.
    regionName: {
      'ap-southeast-7': 'กรุงเทพฯ',
      'ap-southeast-1': 'สิงคโปร์',
      'ap-northeast-1': 'โตเกียว',
      'ap-northeast-3': 'โอซาก้า',
      global: 'ทั่วโลก',
    },
    modeLabel: 'ประเภท',
    allModes: 'ทุกประเภท',
    modeChat: 'บทสนทนา',
    modeEmbedding: 'Embedding',
    modeImage: 'ภาพ',
    modeVideo: 'วิดีโอ',
    modalitiesLabel: 'รูปแบบข้อมูล',
    modalityText: 'ข้อความ',
    modalityAudio: 'เสียง',
    modalityFile: 'ไฟล์',
    sortLabel: 'เรียงตาม',
    sortInputAsc: 'ราคาอินพุต: ต่ำไปสูง',
    sortInputDesc: 'ราคาอินพุต: สูงไปต่ำ',
    sortOutputAsc: 'ราคาเอาต์พุต: ต่ำไปสูง',
    sortOutputDesc: 'ราคาเอาต์พุต: สูงไปต่ำ',
    sortNameAsc: 'ชื่อโมเดล: A–Z',
    sortNameDesc: 'ชื่อโมเดล: Z–A',
    emptyTitle: 'ไม่พบโมเดลที่ตรงกับคำค้นหา',
    emptyBody: 'ลองล้างคำค้นหา หรือขยายตัวกรองผู้ให้บริการ / region',
    input: 'อินพุต',
    output: 'เอาต์พุต',
    perMillionTok: '/ 1M โทเคน',
    noStreaming: 'ไม่รองรับ streaming',
    pageOf: (page, count) => `หน้า ${page} จาก ${count}`,
    modelCount: (n) => `${n} โมเดล`,
    inputPrice: 'ราคาอินพุต',
    outputPrice: 'ราคาเอาต์พุต',
    cacheRead: 'Cache read',
    cacheWrite: 'Cache write',
    contextWindow: 'Context window',
    tokensSuffix: 'โทเคน',
    streaming: 'Streaming',
    supported: 'รองรับ',
    notSupported: 'ไม่รองรับ',
    medianTtft: 'TTFT เฉลี่ย (median)',
    msFromBangkok: (ms) => `${ms} ms วัดจากกรุงเทพฯ`,
    medianThroughput: 'Throughput เฉลี่ย (median)',
    tokSecFromBangkok: (tps) => `${tps} token/วินาที วัดจากกรุงเทพฯ`,
    availableRegions: 'Region ที่ใช้งานได้',
    freeBadge: 'ใช้ฟรี',
    freeUntil: (date) => `ฟรีจนถึง ${date} — ไม่มีค่าใช้จ่ายในช่วงโปรโมชั่นนี้`,
    filterFree: 'ใช้ฟรี',
    filterPromo: 'มีโปรโมชัน',
    filterReset: 'รีเซต',
    promoBadge: (days) => `โปร: เหลือ ${days} วัน`,
    showingAll: (n) => `แสดงทั้งหมด ${n} โมเดล`,
    matchingFilters: (n) => `พบ ${n} โมเดลที่ตรงเงื่อนไข`,
    quickStart: {
      title: 'Quick Start',
      subtitle: 'โค้ดพร้อมใช้สำหรับเรียกโมเดลนี้ผ่าน OpenAI-compatible API ของเรา',
      back: '← กลับ',
      tryThisModel: 'ลองใช้โมเดลนี้',
      step1Title: 'เตรียม API key',
      step1NoKey: 'สร้าง API key จากหน้า API Keys แล้วตั้งเป็น environment variable:',
      step1HasKey: 'ใช้ API key ที่คุณสร้างไว้แล้ว',
      createKeyCta: 'สร้าง API key',
      keysLoadError: 'ตรวจสอบ API key ไม่สำเร็จ เปิดหน้า API Keys เพื่อสร้างหรือดูคีย์ที่มีอยู่',
      keysPageCta: 'เปิดหน้า API Keys',
      envBash: 'Bash / zsh',
      envPowerShell: 'PowerShell',
      installLabel: 'ติดตั้งไลบรารี openai ทางการก่อน:',
      step2Title: 'เรียกใช้ครั้งแรก',
      step2IntroPrefix: 'ใช้',
      step2IntroSuffix: 'กับ LLMR gateway:',
      tabCurl: 'cURL',
      tabPython: 'Python',
      tabNode: 'Node / TypeScript',
      gatewayUnavailable: 'Gateway URL ยังไม่ได้ตั้งค่าใน build นี้ — ไม่สามารถแสดงตัวอย่างโค้ดได้',
    },
  },
  authChatPreviews: {
    gpt: {
      user: 'สรุปสัญญานี้เป็นหัวข้อย่อยให้หน่อย',
      assistant: 'นี่คือข้อกำหนดสำคัญ เงื่อนไขต่ออายุ และขีดจำกัดความรับผิด…',
      placeholder: 'ถามอะไรก็ได้',
    },
    claude: {
      user: 'ช่วยรีวิว pull request เรื่อง security',
      assistant: 'พบ 2 จุดที่น่ากังวล: input ใน auth handler ยังไม่ sanitise…',
      placeholder: 'มีอะไรให้ช่วยวันนี้?',
    },
    gemini: {
      user: 'เขียนอีเมลอัปเดตผลิตภัณฑ์ให้ลูกค้า API',
      assistant: 'หัวข้อ: โมเดลใหม่และ latency ต่ำลงในสิงคโปร์…',
      placeholder: 'ถาม Gemini',
    },
    aliFw: {
      user: 'ส่งคำขอนี้ไป endpoint Qwen ที่ถูกที่สุด',
      assistant: 'ใช้ Qwen ผ่าน Fireworks — ถูกกว่าเส้นทาง default 42%',
      placeholder: 'ส่งข้อความถึง Qwen…',
    },
  },
  authSky: {
    planeBanners: [
      'IC Lueur Ai',
      'จ่ายตามใช้จริง',
      'API key ในไม่กี่นาที',
      'Gateway เดียว หลายโมเดล',
      'ดูการใช้งานแบบเรียลไทม์',
      'ไม่ต้องผ่านขั้นตอนจัดซื้อ',
    ],
  },
  signIn: {
    twoStepTitle: 'การยืนยันตัวตนแบบสองขั้นตอน',
    signInTitle: 'เข้าสู่ระบบ',
    ledeRecovery: 'กรอก recovery code ที่คุณบันทึกไว้',
    ledeAuthenticator: 'กรอกรหัสจากแอป authenticator ของคุณ',
    ledeSignIn: 'เข้าถึงยอดเครดิต, API keys และการใช้งานของคุณ',
    errTooMany: 'พยายามหลายครั้งเกินไป กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
    errCredentials: 'อีเมลและรหัสผ่านไม่ตรงกัน',
    errRecoveryInvalid: 'recovery code นี้ไม่ถูกต้อง หรือถูกใช้ไปแล้ว',
    errCodeInvalid: 'รหัสนี้ไม่ถูกต้อง',
    recoveryCodeLabel: 'Recovery code',
    authCodeLabel: 'รหัสจากแอป authenticator ของคุณ',
    otpCodeLabel: 'กรอกรหัส 6 หลัก',
    checking: 'กำลังตรวจสอบ…',
    continue: 'ดำเนินการต่อ',
    useRecoveryInstead: 'ใช้ recovery code แทน',
    useAuthenticatorInstead: 'ใช้แอป authenticator แทน',
    recoveryHelp:
      'Recovery code แต่ละรหัสใช้ได้เพียงครั้งเดียว หากคุณทำทั้งสองอย่างหาย ผู้ดูแลทีมสามารถรีเซ็ตการยืนยันตัวตนสองขั้นตอนให้ได้ — เราไม่สามารถทำให้จากอีเมลเพียงอย่างเดียว',
    methodName: (method) => {
      switch (method) {
        case 'totp':
          return 'แอป authenticator';
        case 'sms':
          return 'SMS';
        case 'email_otp':
          return 'อีเมล';
        default:
          return method;
      }
    },
    ledeOTPSent: (destinationMasked) => `เราส่งรหัสไปที่ ${destinationMasked} แล้ว`,
    sendingCode: 'กำลังส่งรหัส…',
    resendCode: 'ส่งรหัสอีกครั้ง',
    resendSent: 'ส่งอีกครั้งแล้ว',
    errSendFailed: 'ไม่สามารถส่งรหัสได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
    switchMethod: 'ใช้วิธีอื่น',
    chooseMethodLede: 'ต้องการรับรหัสทางไหน?',
    emailLabel: 'อีเมล',
    passwordLabel: 'รหัสผ่าน',
    signingIn: 'กำลังเข้าสู่ระบบ…',
    signIn: 'เข้าสู่ระบบ',
    noAccount: 'ยังไม่มีบัญชี?',
    signUpLink: 'สมัครสมาชิก',
    errEmailNotVerified: 'กรุณายืนยันอีเมลก่อนเข้าสู่ระบบ — ตรวจสอบกล่องจดหมายของคุณ หรือขอลิงก์ใหม่',
    errTwoFactorSetupRequired: 'รหัสผ่านของคุณถูกต้อง แต่บัญชีนี้ต้องตั้งค่าแอปยืนยันตัวตนก่อนจึงจะเข้าสู่ระบบได้ กรุณาติดต่อผู้ดูแลองค์กรหรือฝ่ายสนับสนุน',
    welcomeEyebrow: 'ยินดีต้อนรับ',
    welcomeHeadline: 'ยอดเครดิต, API\u00A0keys\nและการใช้งานของคุณในที่เดียว',
    welcomeBody: 'เข้าสู่ระบบเพื่อจัดการบัญชีและดูยอดใช้จ่ายของคุณได้ทันที',
    rememberMe: 'จดจำฉันไว้',
    forgotPassword: 'ลืมรหัสผ่าน?',
    orDivider: 'or',
    signInWithGoogle: 'เข้าสู่ระบบด้วย Google',
    signInWithMicrosoft: 'เข้าสู่ระบบด้วย Microsoft',
    oauthError: (code) => {
      switch (code) {
        case 'google_denied':
          return 'ยกเลิกการเข้าสู่ระบบด้วย Google แล้ว';
        case 'google_email_unverified':
          return 'Google แจ้งว่าอีเมลนี้ยังไม่ได้ยืนยัน กรุณายืนยันกับ Google ก่อน หรือเข้าสู่ระบบด้วยรหัสผ่านแทน';
        case 'google_email_conflict':
          return 'อีเมลนี้เป็นของบัญชีที่ใช้เข้าสู่ระบบด้วย Google ไม่ได้ กรุณาเข้าสู่ระบบด้วยรหัสผ่านแทน';
        case 'google_not_configured':
          return 'เข้าสู่ระบบด้วย Google เร็วๆ นี้ ตอนนี้กรุณาเข้าสู่ระบบด้วยรหัสผ่านไปก่อน';
        case 'google_unavailable':
          return 'การเข้าสู่ระบบด้วย Google ใช้งานไม่ได้ในขณะนี้ ลองใหม่อีกครั้ง หรือเข้าสู่ระบบด้วยรหัสผ่านแทน';
        case 'microsoft_denied':
          return 'ยกเลิกการเข้าสู่ระบบด้วย Microsoft แล้ว';
        case 'microsoft_email_missing':
          return 'Microsoft ไม่ได้ส่งอีเมลที่ยืนยันแล้วมาให้ กรุณาเข้าสู่ระบบด้วยรหัสผ่านแทน';
        case 'microsoft_email_conflict':
          return 'อีเมลนี้เป็นของบัญชีที่ใช้เข้าสู่ระบบด้วย Microsoft ไม่ได้ กรุณาเข้าสู่ระบบด้วยรหัสผ่านแทน';
        case 'microsoft_not_configured':
          return 'เข้าสู่ระบบด้วย Microsoft เร็วๆ นี้ ตอนนี้กรุณาเข้าสู่ระบบด้วยรหัสผ่านไปก่อน';
        case 'microsoft_unavailable':
          return 'การเข้าสู่ระบบด้วย Microsoft ใช้งานไม่ได้ในขณะนี้ ลองใหม่อีกครั้ง หรือเข้าสู่ระบบด้วยรหัสผ่านแทน';
        case 'oauth_expired':
          return 'ลิงก์เข้าสู่ระบบหมดอายุแล้ว กรุณาลองใหม่อีกครั้ง';
        default:
          return 'การเข้าสู่ระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง';
      }
    },
    showPassword: 'แสดง',
    hidePassword: 'ซ่อน',
  },
  passwordValidation: {
    minLength: 'อย่างน้อย 8 ตัวอักษร',
    special: 'อย่างน้อย 1 อักขระพิเศษ',
    uppercase: 'อย่างน้อย 1 ตัวพิมพ์ใหญ่',
    number: 'อย่างน้อย 1 ตัวเลข',
  },
  signUp: {
    title: 'สร้างบัญชีของคุณ',
    lede: 'รับ API key และเริ่มจ่ายตามการใช้งานจริง',
    accountTypeLabel: 'ประเภทบัญชี',
    accountTypeIndividual: 'บุคคล',
    accountTypeOrganization: 'องค์กร',
    emailLabel: 'อีเมล',
    emailLabelOrganization: 'อีเมลองค์กร',
    displayNameLabel: 'ชื่อ (ไม่บังคับ)',
    displayNameLabelOrganization: 'ชื่อผู้ติดต่อ',
    orgNameLabel: 'ชื่อบริษัท',
    taxIdLabel: 'เลขประจำตัวผู้เสียภาษี',
    taxIdHint: 'เลข 13 หลักตามที่จดทะเบียนกับกรมสรรพากร',
    phoneLabel: 'เบอร์โทรศัพท์',
    countryCodeLabel: 'รหัสประเทศ',
    passwordLabel: 'รหัสผ่าน',
    passwordHint: 'อย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ',
    confirmPasswordLabel: 'ยืนยันรหัสผ่าน',
    creatingAccount: 'กำลังสร้างบัญชี…',
    createAccount: 'สร้างบัญชี',
    errTooMany: 'พยายามหลายครั้งเกินไป กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
    errWeakPassword:
      'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ และต้องไม่ใช่อีเมลของคุณ',
    errInvalidPhone: 'กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง',
    errPhoneTaken: 'เบอร์โทรศัพท์นี้ถูกใช้สมัครแล้ว',
    errEmailTaken: 'อีเมลนี้ถูกใช้สมัครแล้ว กรุณาเข้าสู่ระบบ หรือใช้อีเมลอื่น',
    errEmailRequired: 'กรุณากรอกอีเมล',
    errCheckingEmail: 'กำลังตรวจสอบอีเมล…',
    errPhoneRequired: 'กรุณากรอกเบอร์โทรศัพท์',
    errPasswordRequired: 'กรุณากรอกรหัสผ่าน',
    errPasswordMismatch: 'รหัสผ่านไม่ตรงกัน',
    errGeneric: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
    errTermsNotAccepted: 'คุณต้องยอมรับข้อกำหนดการให้บริการและนโยบายความเป็นส่วนตัวก่อนสร้างบัญชี',
    errOrgNameRequired: 'กรุณากรอกชื่อบริษัท',
    errInvalidTaxId: 'กรุณากรอกเลขประจำตัวผู้เสียภาษีให้ถูกต้อง (13 หลัก)',
    errContactNameRequired: 'กรุณากรอกชื่อผู้ติดต่อ',
    promoCodeLabel: 'โค้ดโปรโมชั่น (ถ้ามี)',
    promoCodeHint: 'มีการ์ดจากงานอีเวนต์ไหม? กรอกโค้ดได้เลย — มูลค่าเป็นเซอร์ไพรส์ จะเห็นก็ต่อเมื่อยืนยันอีเมลแล้ว',
    errCheckingPromoCode: 'กำลังตรวจสอบโค้ด…',
    errPromoCodeNotFound: 'ไม่พบโค้ดนี้ในระบบ ลองตรวจสอบโค้ดบนการ์ดอีกครั้ง',
    errPromoCodeUsed: 'โค้ดนี้ถูกใช้ไปแล้ว',
    errPromoCodeNotYetValid: 'โค้ดนี้ยังไม่เปิดให้ใช้งาน',
    errPromoCodeExpired: 'โค้ดนี้หมดอายุแล้ว',
    referralSourceLabel: 'รู้จักเราจากช่องทางไหน? (ถ้ามี)',
    referralSourceOptions: {
      aiExpo: 'AI Expo Thailand 2026 (Oct)',
      facebook: 'Facebook',
      instagram: 'Instagram',
      line: 'LINE',
      friend: 'เพื่อน/คนรู้จักแนะนำ',
      googleSearch: 'ค้นหาใน Google',
      other: 'อื่นๆ',
    },
    referralSourceOtherPlaceholder: 'ระบุช่องทาง',
    agreePrefix: 'ฉันยอมรับ',
    agreeTerms: 'ข้อกำหนดการให้บริการ',
    agreeAnd: 'และ',
    agreePrivacy: 'นโยบายความเป็นส่วนตัว',
    pendingTitle: 'ตรวจสอบอีเมลของคุณ',
    pendingBody: (email) => `เราส่งลิงก์ยืนยันไปที่ ${email} แล้ว กรุณาคลิกลิงก์เพื่อสร้างบัญชีให้เสร็จสมบูรณ์`,
    resendButton: 'ส่งอีเมลอีกครั้ง',
    resendBusy: 'กำลังส่ง…',
    resendSent: 'ส่งแล้ว — ตรวจสอบกล่องจดหมายของคุณ',
    haveAccount: 'มีบัญชีอยู่แล้ว?',
    signInLink: 'เข้าสู่ระบบ',
    showPassword: 'แสดง',
    hidePassword: 'ซ่อน',
    welcomeEyebrow: 'เริ่มต้นใช้งาน',
    welcomeHeadline: 'สร้างบัญชี\nและรับ API\u00A0key ได้ภายในไม่กี่นาที',
    welcomeBody: 'จ่ายตามการใช้งานจริง ไม่มีขั้นต่ำ ไม่ต้องผ่านขั้นตอนจัดซื้อ',
  },
  verifyEmail: {
    verifyingTitle: 'กำลังยืนยันอีเมลของคุณ…',
    verifyingBody: 'กรุณารอสักครู่',
    successTitle: 'ยืนยันอีเมลสำเร็จ',
    successBody: 'บัญชีของคุณพร้อมใช้งานแล้ว กรุณาเข้าสู่ระบบเพื่อดำเนินการต่อ',
    promoCredited: (amount) => `แลกโค้ดโปรโมชั่นสำเร็จ — ได้รับ ฿${amount} เข้าบัญชีแล้ว`,
    goToSignIn: 'ไปที่หน้าเข้าสู่ระบบ',
    failedTitle: 'ลิงก์นี้ใช้งานไม่ได้แล้ว',
    failedBody: 'ลิงก์อาจหมดอายุหรือถูกใช้ไปแล้ว กรุณาขอลิงก์ใหม่ด้านล่าง',
    resendEmailLabel: 'อีเมล',
    resendButton: 'ส่งลิงก์ใหม่',
    resendBusy: 'กำลังส่ง…',
    resendSent: 'ส่งแล้ว — ตรวจสอบกล่องจดหมายของคุณ',
    missingTokenBody: 'ลิงก์นี้ไม่มี token สำหรับยืนยัน',
    welcomeEyebrow: 'ใกล้เสร็จแล้ว',
    welcomeHeadline: 'อีกแค่คลิกเดียว\nบัญชีของคุณก็พร้อมใช้งาน',
    welcomeBody: 'เรากำลังยืนยันว่าเป็นคุณจริงๆ\nก่อนเข้าสู่ระบบครั้งแรก',
  },
  twoFactorRequired: {
    eyebrow: 'อีกหนึ่งขั้นตอน',
    title: 'ตั้งค่าแอปยืนยันตัวตน',
    body: 'บัญชีของคุณดูแลองค์กร จึงต้องใช้แอปยืนยันตัวตน (authenticator app) ทุกครั้งที่เข้าสู่ระบบ กรุณาตั้งค่าตอนนี้เพื่อเปิดใช้งานบัญชีให้เสร็จสมบูรณ์',
    signOutInstead: 'ออกจากระบบแทน',
  },
  forgotPassword: {
    title: 'ลืมรหัสผ่าน',
    lede: 'กรอกอีเมลของบัญชี หากมีบัญชีอยู่ เราจะส่งลิงก์สำหรับตั้งรหัสผ่านใหม่',
    emailLabel: 'อีเมล',
    sendLink: 'ส่งลิงก์รีเซ็ต',
    sending: 'กำลังส่ง…',
    sentTitle: 'ตรวจสอบอีเมลของคุณ',
    sentBody: (email) =>
      `หากมีบัญชีสำหรับ ${email} ลิงก์รีเซ็ตจะถูกส่งไปแล้ว ลิงก์หมดอายุใน 1 ชั่วโมง`,
    backToSignIn: 'กลับไปเข้าสู่ระบบ',
    errTooMany: 'ส่งคำขอมากเกินไป กรุณารอสักครู่แล้วลองใหม่',
    errGeneric: 'เกิดข้อผิดพลาด กรุณาลองใหม่',
    welcomeEyebrow: 'ยินดีต้อนรับ',
    welcomeHeadline: 'รีเซ็ตการเข้าถึง\nบัญชีของคุณ',
    welcomeBody: 'เราจะส่งลิงก์ใช้ครั้งเดียวทางอีเมล\nเพื่อให้คุณตั้งรหัสผ่านใหม่ได้',
  },
  resetPassword: {
    title: 'ตั้งรหัสผ่านใหม่',
    lede: 'เลือกรหัสผ่านที่แข็งแรง จากนั้นเข้าสู่ระบบด้วยรหัสผ่านใหม่',
    passwordLabel: 'รหัสผ่านใหม่',
    passwordHint: 'อย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ',
    confirmPasswordLabel: 'ยืนยันรหัสผ่าน',
    savePassword: 'บันทึกรหัสผ่าน',
    saving: 'กำลังบันทึก…',
    successTitle: 'อัปเดตรหัสผ่านแล้ว',
    successBody: 'เข้าสู่ระบบด้วยรหัสผ่านใหม่เพื่อดำเนินการต่อ',
    goToSignIn: 'ไปที่หน้าเข้าสู่ระบบ',
    failedTitle: 'ลิงก์นี้ใช้งานไม่ได้แล้ว',
    missingTokenBody: 'ลิงก์นี้ไม่มี token สำหรับรีเซ็ตรหัสผ่าน',
    invalidTokenBody:
      'ลิงก์นี้อาจหมดอายุแล้ว ถูกใช้ไปแล้ว หรือมีการขอลิงก์ใหม่กว่านี้ภายหลัง กรุณาขอลิงก์ใหม่',
    checkingToken: 'กำลังตรวจสอบลิงก์…',
    requestNewLink: 'ขอลิงก์ใหม่',
    errPasswordMismatch: 'รหัสผ่านไม่ตรงกัน',
    errWeakPassword:
      'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ',
    errInvalidToken: 'ลิงก์นี้อาจหมดอายุแล้ว ถูกใช้ไปแล้ว หรือมีการขอลิงก์ใหม่กว่านี้ภายหลัง',
    errTooMany: 'ลองมากเกินไป กรุณารอสักครู่แล้วลองใหม่',
    errGeneric: 'เกิดข้อผิดพลาด กรุณาลองใหม่',
    showPassword: 'แสดง',
    hidePassword: 'ซ่อน',
    welcomeEyebrow: 'ใกล้เสร็จแล้ว',
    welcomeHeadline: 'ตั้งรหัสผ่านใหม่\nแล้วกลับเข้าใช้งานได้',
    welcomeBody: 'หลังบันทึก ให้เข้าสู่ระบบด้วยรหัสผ่านใหม่\n— เซสชันบนอุปกรณ์อื่นจะถูกออกจากระบบ',
  },
  acceptInvite: {
    title: 'ยอมรับคำเชิญ',
    lede: 'คุณได้รับเชิญให้เข้าร่วมทีมบน IC Lueur Ai กรุณาตั้งค่าบัญชีเพื่อยอมรับคำเชิญ',
    ledeExisting: 'คุณได้รับเชิญให้เข้าร่วมทีมบน IC Lueur Ai กดยอมรับเพื่อเพิ่มทีมนี้เข้าบัญชีของคุณ',
    displayNameLabel: 'ชื่อ (ไม่บังคับ)',
    phoneLabel: 'เบอร์โทรศัพท์',
    countryCodeLabel: 'รหัสประเทศ',
    passwordLabel: 'รหัสผ่าน',
    passwordHint: 'อย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ',
    confirmPasswordLabel: 'ยืนยันรหัสผ่าน',
    createAccount: 'ยอมรับและสร้างบัญชี',
    creatingAccount: 'กำลังตั้งค่าบัญชี…',
    acceptJoin: 'ยอมรับคำเชิญ',
    accepting: 'กำลังเข้าร่วมทีม…',
    showPassword: 'แสดง',
    hidePassword: 'ซ่อน',
    errPasswordMismatch: 'รหัสผ่านไม่ตรงกัน',
    errWeakPassword:
      'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ',
    errInvalidPhone: 'กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง',
    errPhoneTaken: 'เบอร์โทรศัพท์นี้ถูกใช้สมัครแล้ว',
    errAccountExists: 'อีเมลนี้มีบัญชีอยู่แล้ว — กรุณาเข้าสู่ระบบแทน',
    errAlreadyMember: 'บัญชีนี้เป็นสมาชิกของทีมนี้อยู่แล้ว',
    errTooMany: 'พยายามหลายครั้งเกินไป กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
    errGeneric: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
    missingTokenTitle: 'ลิงก์นี้ไม่มี token สำหรับคำเชิญ',
    missingTokenBody: 'กรุณาขอให้ผู้เชิญส่งลิงก์ให้ใหม่อีกครั้ง',
    checkingToken: 'กำลังตรวจสอบคำเชิญ…',
    invalidTokenTitle: 'คำเชิญนี้ใช้งานไม่ได้แล้ว',
    invalidTokenBody: 'อาจหมดอายุหรือถูกใช้ไปแล้ว กรุณาขอให้ผู้ดูแลทีมส่งคำเชิญใหม่',
    goToSignIn: 'ไปที่หน้าเข้าสู่ระบบ',
    welcomeEyebrow: 'คุณได้รับเชิญ',
    welcomeHeadline: 'เข้าร่วมทีมของคุณ\nบน IC Lueur Ai',
    welcomeBody: 'อีกแค่ขั้นตอนเดียว คุณก็จะเห็น key, การใช้งาน\nและกระเป๋าเครดิตร่วมกับทีมของคุณ',
  },
  topUp: {
    subtitle: 'เติมเครดิตล่วงหน้าผ่าน PromptPay หรือบัตรเครดิต/เดบิต',
    currentBalance: 'ยอดคงเหลือปัจจุบัน',
    asOf: (date) => `ข้อมูล ณ ${date}`,
    balanceRestricted: (isB2B) =>
      isB2B ? 'เฉพาะ admin และ owner เท่านั้นที่ดูยอดคงเหลือขององค์กรได้' : 'เฉพาะ admin และ owner เท่านั้นที่ดูยอดคงเหลือของบัญชีได้',
    topUpRestrictedTitle: 'เฉพาะ admin และ owner เท่านั้นที่เติมเงินได้',
    topUpRestrictedBody: (isB2B) =>
      isB2B ? 'ให้ admin หรือ owner ขององค์กรคุณเป็นคนเติมเงินแทน' : 'ให้ admin หรือ owner ของบัญชีคุณเป็นคนเติมเงินแทน',
    inProgress: 'กำลังดำเนินการเติมเงิน',
    backToForm: 'ย้อนกลับไปแก้ไขจำนวนเงิน',
    qrAlt: 'QR code ของ PromptPay สำหรับรายการเติมเงินนี้',
    // Translated per the PO's ruling: this is about the customer's own
    // money, exactly where ambiguity generates support tickets.
    status: {
      pending: 'รอการชำระเงิน',
      paid: 'ชำระเงินแล้ว',
      expired: 'หมดอายุ',
      failed: 'ไม่สำเร็จ',
    },
    scanNote:
      'สแกนด้วยแอปธนาคารของคุณ เครดิตจะถูกเติมให้อัตโนมัติเมื่อชำระเงินสำเร็จ — หน้านี้ตรวจสอบทุกไม่กี่วินาที คุณไม่ต้องรีเฟรชเอง',
    companyQrAlt: 'QR code สำหรับโอนเงินเข้าบัญชีพร้อมเพย์ของบริษัทโดยตรง',
    companyQrNote:
      'ผู้ให้บริการชำระเงินตามปกติของเราไม่สามารถใช้งานได้ในขณะนี้ QR นี้จึงเป็นการโอนเข้าบัญชีพร้อมเพย์ของบริษัทโดยตรง หลังจากโอนเงินแล้ว กรุณาอัปโหลดสลิปด้านล่าง เจ้าหน้าที่จะเติมเครดิตให้หลังจากตรวจสอบกับข้อมูลธนาคารแล้ว ขั้นตอนนี้อาจใช้เวลานานกว่าการเติมเครดิตแบบทันทีตามปกติ',
    companyQrUploadPrompt: 'โอนเงินแล้ว? อัปโหลดสลิปที่นี่',
    expires: (date) => `หมดอายุ ${date}`,
    saveQrCode: 'บันทึก QR Code',
    saveQrSaving: 'กำลังบันทึก…',
    saveQrError: 'บันทึก QR Code ไม่สำเร็จ ลองใหม่อีกครั้ง หรือกดค้างที่รูปเพื่อบันทึก',
    saveQrImageTitle: 'IC Lueur Ai · PromptPay',
    creditAdded: 'เครดิตถูกเพิ่มเข้ายอดคงเหลือของคุณด้านบนแล้ว',
    expired: 'รายการเติมเงินนี้หมดอายุก่อนที่จะได้รับการชำระเงิน',
    failed: 'รายการเติมเงินนี้ไม่สำเร็จ',
    startAnother: 'เริ่มเติมเงินรายการใหม่',
    addCredit: 'เติมเครดิต',
    custom: 'กำหนดเอง',
    stepAmountLabel: 'จำนวนเงิน',
    amountLabel: 'จำนวนเงิน (บาท)',
    amountPlaceholder: 'เช่น 750.00',
    vatInclusiveNote: (amount) => `รวม VAT 7% ต้องชำระ ฿${amount}`,
    vatSummaryBeforeConfirm: (amount) => `ยอดที่ต้องชำระจริง (รวม VAT 7%) คือ ฿${amount}`,
    paymentMethodLabel: 'ช่องทางการชำระเงิน',
    methodPromptPay: 'พร้อมเพย์',
    methodCard: 'บัตรเครดิต/เดบิต',
    methodPromptPaySubtitle: '(QR Code)',
    methodCardSubtitle: '(Credit Card)',
    paidByPromptPay: 'ชำระผ่าน PromptPay — QR code จะปรากฏหลังจากคุณดำเนินการต่อ',
    paidByPromptPayExpiry: (minutes) =>
      `QR มีอายุ ${minutes} นาที — หากหมดอายุก่อนชำระ ระบบจะยกเลิกรายการอัตโนมัติ และสถานะในประวัติด้านล่างจะแสดงเป็น「หมดอายุ」(ยังไม่ตัดเงินจากบัญชีคุณ) หากต้องการเติมต่อให้เริ่มรายการใหม่เพื่อรับ QR ใหม่`,
    paidByCard: 'ชำระผ่านบัตร — ธนาคารของคุณอาจขอให้ยืนยันรายการนี้เพิ่มเติม',
    afterContinueBadge: 'ถัดไป',
    afterContinueTitle: 'หลังกดดำเนินการต่อ',
    afterContinueSteps: [
      'ระบบจะแสดง QR Code ให้สแกนผ่านแอปธนาคาร',
      'สแกนแล้วโอนตามจำนวนที่ระบุ',
      'เครดิตจะเข้าอัตโนมัติหลังชำระสำเร็จ (ไม่ต้องกดยืนยันเพิ่ม และไม่ต้องรีเฟรชหน้า)',
      'สถานะในตาราง「ประวัติการเติมเงิน」ด้านล่างจะเปลี่ยนจาก「รอการชำระเงิน」เป็น「ชำระเงินแล้ว」',
    ],
    payHowTitle: 'วิธีชำระด้วย PromptPay',
    payHowSteps: [
      'เปิดแอปธนาคารหรือแอปที่รองรับ PromptPay',
      'สแกน QR นี้ (หรือกด「บันทึก QR」แล้วสแกนจากแกลเลอรี)',
      'ตรวจสอบยอดเงินให้ตรงกับจำนวนที่เติม แล้วยืนยันการโอน',
      'กลับมาที่หน้านี้ — ระบบจะตรวจสถานะให้อัตโนมัติ ไม่ต้องรีเฟรชหน้าจอ',
    ],
    payHowSuccessNote: 'เมื่อชำระสำเร็จ เครดิตจะถูกเพิ่มเข้ายอดคงเหลือด้านบนโดยอัตโนมัติ',
    timeRemaining: (mmss) => `เหลือเวลา ${mmss}`,
    waitingForPayment: 'กำลังรอการชำระเงิน…',
    topUpSuccessToast: 'เติมเงินสำเร็จ เครดิตถูกเพิ่มแล้ว',
    expiredGuidance:
      'รายการนี้หมดอายุแล้ว หากยังไม่ได้ชำระ ให้เริ่มเติมเงินใหม่เพื่อรับ QR ใหม่ หากชำระไปแล้วแต่รายการหมดอายุก่อนระบบรับสถานะ ให้ใช้อัปโหลดสลิปตามช่องทางด้านล่าง',
    cardNameLabel: 'ชื่อบนบัตร',
    cardNamePlaceholder: 'ตามที่พิมพ์อยู่บนบัตร',
    cardNumberLabel: 'หมายเลขบัตร',
    cardNumberPlaceholder: '4242 4242 4242 4242',
    cardExpiryLabel: 'วันหมดอายุ',
    cardCVCLabel: 'CVC',
    cardCVCPlaceholder: '123',
    starting: 'กำลังเริ่ม…',
    continueToPay: (amount) => `ดำเนินการต่อเพื่อชำระ ฿${amount}`,
    enterAmount: 'กรุณากรอกจำนวนเงิน',
    amountMustBePositive: 'จำนวนเงินต้องมากกว่าศูนย์',
    belowMinimum: (min) => `ยอดเติมเงินขั้นต่ำคือ ${min} บาท`,
    notValidTHB: 'จำนวนเงินบาทไม่ถูกต้อง',
    notValidAmount: 'จำนวนเงินไม่ถูกต้อง',
    historyHeading: 'ประวัติการเติมเงิน',
    historyNote:
      'รายการเติมเงินทั้งหมดของบัญชีนี้ เรียงจากล่าสุด เครดิตจะถูกเพิ่มเมื่อได้รับการยืนยันการชำระเงินแล้วเท่านั้น รายการจึงอาจอยู่ในสถานะรอชำระเงินสักระยะก่อนเปลี่ยนเป็นชำระแล้ว',
    historyEmptyTitle: 'ไม่มีรายการเติมเงินในช่วงนี้',
    historyEmptyBody: 'ลองขยายช่วงวันที่',
    historyLoadError: 'ไม่สามารถโหลดประวัติการเติมเงินได้ กรุณาลองใหม่',
    colStarted: 'เริ่มเมื่อ',
    colAmount: 'จำนวนเงิน',
    colAmountVatNote: (vatAmount) => `VAT 7% : ฿${vatAmount}`,
    colStatus: 'สถานะ',
    colInvoice: 'ใบเสร็จ/ใบกำกับภาษี',
    invoiceButton: 'ดาวน์โหลด',
    invoiceGenerating: 'กำลังออกใบเสร็จ…',
    invoiceComingSoon: 'กำลังสร้างเอกสาร ลองใหม่อีกครั้งในอีกสักครู่',
    invoiceDownloadError: 'ไม่สามารถดาวน์โหลดใบกำกับภาษีได้ กรุณาลองใหม่',
    taxInvoiceHeading: 'ข้อมูลใบกำกับภาษี',
    taxInvoiceFullLabel: 'ขอใบกำกับภาษีเต็มรูปแบบ',
    useAccountInfoLabel: 'ใช้ชื่อและอีเมลจากบัญชีของฉัน',
    useTaxProfileLabel: 'ใช้ข้อมูลใบกำกับภาษีล่าสุด',
    buyerNameLabel: 'ชื่อ-นามสกุล',
    buyerNamePlaceholder: 'ชื่อ-นามสกุล',
    buyerCompanyNameLabel: 'ชื่อบริษัท',
    buyerCompanyNamePlaceholder: 'ชื่อบริษัทเต็ม เช่น บริษัท ทดสอบ จำกัด',
    buyerEmailLabel: 'อีเมล',
    buyerEmailPlaceholder: 'you@example.com',
    buyerTypeLabel: 'ประเภทผู้ซื้อ',
    buyerTypeIndividual: 'บุคคลธรรมดา',
    buyerTypeJuristic: 'นิติบุคคล',
    juristicWhtNote:
      'สำหรับบัญชีบุคคล (Individual) ระบบรองรับการออกใบกำกับภาษีในนามนิติบุคคลเพื่อคุมค่าใช้จ่ายเท่านั้น แต่ไม่รองรับกระบวนการหักภาษี ณ ที่จ่าย (Withholding Tax 3%) หากต้องการหัก ณ ที่จ่าย กรุณาเปลี่ยนบัญชีเป็นบัญชีองค์กร (B2B)',
    taxIdLabelIndividual: 'เลขประจำตัวประชาชน',
    taxIdPlaceholderIndividual: 'เลขบัตรประชาชน 13 หลัก',
    taxIdLabelJuristic: 'เลขประจำตัวผู้เสียภาษี',
    taxIdPlaceholderJuristic: 'เลขประจำตัวผู้เสียภาษี 13 หลัก',
    taxIdInvalid: 'เลข 13 หลักไม่ถูกต้อง',
    branchCodeLabel: 'รหัสสาขา',
    branchCodePlaceholder: '00000 สำหรับสำนักงานใหญ่',
    branchCodeInvalid: 'รหัสสาขาต้องเป็นตัวเลข 5 หลัก (00000 สำหรับสำนักงานใหญ่)',
    branchNameLabel: 'ชื่อสาขา',
    branchNamePlaceholder: 'เช่น สาขาสีลม',
    addressHouseNoLabel: 'ที่อยู่',
    addressHouseNoPlaceholder: 'บ้านเลขที่ / อาคาร ถนน',
    addressProvinceLabel: 'จังหวัด',
    addressProvincePlaceholder: 'เลือกจังหวัด',
    addressDistrictLabel: 'อำเภอ/เขต',
    addressDistrictPlaceholder: 'เลือกอำเภอ/เขต',
    addressDistrictSelectProvinceFirst: 'เลือกจังหวัดก่อน',
    addressSubdistrictLabel: 'ตำบล/แขวง',
    addressSubdistrictPlaceholder: 'เลือกตำบล/แขวง',
    addressSubdistrictSelectDistrictFirst: 'เลือกอำเภอ/เขตก่อน',
    addressPostalCodeLabel: 'รหัสไปรษณีย์',
    addressPostalCodePlaceholder: 'กรอกอัตโนมัติเมื่อเลือกตำบล/แขวง',
    addressSelectNoResults: 'ไม่พบรายการ',
    addressSelectTypeHint: 'สามารถพิมพ์เพื่อค้นหาได้',
    taxInvoiceDraftNote: 'ระบบจะออกเอกสารฉบับร่างเพื่อเก็บไว้เป็นหลักฐาน ระบบใบกำกับภาษีอิเล็กทรอนิกส์อัตโนมัติยังไม่เปิดใช้งาน',
    createError: 'ไม่สามารถเริ่มรายการเติมเงินได้',
    preparingPayment: 'กำลังเตรียม QR พร้อมเพย์…',
    preparingCardPayment: 'กำลังเตรียมการชำระเงินด้วยบัตร…',
    awaitingCardConfirmation: 'กำลังยืนยันการชำระเงิน — อาจใช้เวลาสักครู่',
    paymentUnavailable:
      'ไม่สามารถเชื่อมต่อผู้ให้บริการชำระเงินได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่ ยังไม่มีการตัดเงินใดๆ',
    paymentsNotConfigured: 'ระบบชำระเงินยังไม่เปิดให้ใช้งานในตอนนี้ จึงยังไม่มีการตัดเงินใดๆทั้งสิ้น',
    sourceError: 'ผู้ให้บริการชำระเงินไม่สามารถเตรียมรายการนี้ได้ ยังไม่มีการตัดเงินใดๆ',
    paymentExpired: 'รายการชำระเงินนี้หมดอายุก่อนทำรายการสำเร็จ กรุณาเริ่มเติมเงินใหม่เพื่อรับ QR ใหม่',
    uploadSlipButton: 'จ่ายไปแล้ว? อัปโหลดสลิป',
    uploadSlipModalTitle: 'อัปโหลดสลิปการชำระเงิน',
    uploadSlipInstructions:
      'ถ้าคุณโอนเงินตามยอดที่ระบุไว้ในหน้า QR (ซึ่งรวม VAT 7% แล้ว) สำหรับรายการเติมเงินนี้ไปแล้ว ให้อัปโหลดรูปสลิปการโอน เราจะตรวจสอบกับข้อมูลจริงของธนาคาร และเติมเครดิตให้หลังจากเจ้าหน้าที่ตรวจสอบแล้ว — ขั้นตอนนี้ไม่ใช่การเติมเงินทันที',
    uploadSlipChooseFile: 'เลือกรูปภาพ',
    uploadSlipChooseAnother: 'เลือกรูปอีกครั้ง',
    uploadSlipQrFound: 'พบ QR code บนสลิปแล้ว',
    uploadSlipQrNotFound: 'ไม่พบ QR code บนรูปนี้ ยังสามารถอัปโหลดได้ เจ้าหน้าที่จะตรวจสอบด้วยมือ',
    uploadSlipSubmit: 'ส่งสลิป',
    uploadSlipSubmitting: 'กำลังส่ง…',
    uploadSlipSuccess:
      'ได้รับสลิปแล้ว เราจะเติมเครดิตให้หลังจากเจ้าหน้าที่ตรวจสอบกับข้อมูลธนาคารแล้ว อาจใช้เวลาสักครู่ ไม่ใช่ทันที',
    uploadSlipNotImage: 'กรุณาเลือกไฟล์รูปภาพ',
    uploadSlipTooLarge: 'ไฟล์รูปภาพนี้ใหญ่เกินไป (สูงสุด 6 MB)',
    uploadSlipError: 'ไม่สามารถส่งสลิปได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่',
    colAction: 'อัปโหลดสลิปการชำระเงิน',
  },
  whtClaim: {
    colSelect: 'เลือกเพื่อส่ง',
    colSubmitDoc: 'ส่งใบ 50 ทวิ (ทีละรายการ)',
    selectAllLabel: 'เลือกทั้งหมด',
    claimButton: 'เคลม หัก ณ ที่จ่าย',
    claimSelectedButton: (n) => `เคลม หัก ณ ที่จ่าย (เลือก ${n} รายการ)`,
    modalTitle: 'เคลมเครดิต หัก ณ ที่จ่าย',
    modalIntro:
      'ใบ 50 ทวิ หนึ่งใบสามารถครอบคลุม top-up เดียวหรือหลายรายการพร้อมกันได้ (เช่น รวมทั้งเดือน) — เลือก top-up ที่เอกสารนี้ครอบคลุมด้านล่าง',
    itemsSelectedLabel: (n) => `Top-up ที่เอกสารนี้ครอบคลุม (เลือกแล้ว ${n} รายการ)`,
    expectedWHTLabel: (amount) => `3% = ฿${amount}`,
    computedTotalLabel: (amount) => `ยอดรวมที่คำนวณได้: ฿${amount}`,
    docNumberLabel: 'เลขที่เอกสาร',
    docNumberPlaceholder: 'ตามที่ระบุในเอกสาร',
    docDateLabel: 'วันที่เอกสาร',
    declaredAmountLabel: 'ยอดหัก ณ ที่จ่ายที่แจ้ง (บาท)',
    declaredAmountPlaceholder: 'ตามที่แสดงในเอกสาร',
    declaredTaxIdLabel: 'เลขผู้เสียภาษีในเอกสาร',
    declaredTaxIdPlaceholder: 'เลขผู้เสียภาษี 13 หลัก',
    declaredTaxIdInvalid: 'เลขผู้เสียภาษีนี้ดูไม่ถูกต้อง (ต้องเป็นเลข 13 หลัก)',
    fileLabel: 'ไฟล์เอกสาร',
    chooseFile: 'เลือกไฟล์',
    chooseAnotherFile: 'เลือกไฟล์อื่น',
    fileTooLarge: 'ไฟล์นี้ใหญ่เกินไป (สูงสุด 10 MB)',
    fileUnsupportedType: 'อัปโหลดรูปภาพ JPEG/PNG/WebP/GIF หรือไฟล์ PDF',
    fileError: 'ไม่สามารถอ่านไฟล์นี้ได้ กรุณาลองใหม่',
    submitButton: 'ส่งคำขอเคลม',
    submitting: 'กำลังส่ง…',
    submitError: 'ไม่สามารถส่งคำขอนี้ได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่',
    successTitle: 'ส่งคำขอเคลมแล้ว',
    successBody: (amount) => `ส่งให้เจ้าหน้าที่ตรวจสอบแล้ว หากอนุมัติ ฿${amount} จะถูกเพิ่มเข้า wallet ของคุณ`,
    warningsIntro: 'เจ้าหน้าที่จะตรวจสอบคำขอนี้ละเอียดขึ้นเพราะ:',
    warningLabels: {
      org_tax_id_not_on_file: 'องค์กรของคุณยังไม่มีเลขผู้เสียภาษีในระบบให้ตรวจสอบ',
      declared_amount_mismatch: 'ยอดที่แจ้งต่างจากยอดที่คำนวณได้เกินกว่าการปัดเศษปกติ',
      doc_date_before_topup: 'วันที่เอกสารอยู่ก่อนวันที่ top-up ที่ครอบคลุม',
      doc_date_too_far_after_topup: 'วันที่เอกสารห่างจาก top-up ที่ครอบคลุมเกิน 90 วัน',
    },
    eligibleLoadError: 'ไม่สามารถโหลดรายการ top-up ที่เคลม หัก ณ ที่จ่าย ได้ได้',
    historyHeading: 'คำขอเคลม หัก ณ ที่จ่าย',
    historyLede: 'เอกสารทุกใบที่คุณเคยส่งมา และผลลัพธ์ของแต่ละใบ',
    historyEmpty: 'ยังไม่มีคำขอเคลม หัก ณ ที่จ่าย',
    historyLoadError: 'ไม่สามารถโหลดประวัติคำขอเคลมได้',
    colDocNumber: 'เลขที่เอกสาร',
    colDocDate: 'วันที่เอกสาร',
    colDeclaredAmount: 'ยอดที่แจ้ง',
    colComputedAmount: 'ยอดที่คำนวณได้',
    colStatus: 'สถานะ',
    colReason: 'เหตุผล',
    statusLabels: {
      pending_review: 'รอตรวจสอบ',
      approved: 'อนุมัติแล้ว',
      rejected: 'ปฏิเสธแล้ว',
    },
  },
  promoCode: {
    title: 'มีการ์ดโปรโมชั่นไหม?',
    description: 'กรอกโค้ดที่พิมพ์อยู่บนการ์ด มูลค่าเป็นเซอร์ไพรส์ — จะเห็นก็ต่อเมื่อแลกสำเร็จแล้ว',
    placeholder: 'กรอกโค้ด',
    submit: 'แลกโค้ด',
    submitting: 'กำลังแลกโค้ด…',
    successToast: (amount) => `ได้รับ ฿${amount} — เพิ่มเข้ายอดเครดิตแล้ว`,
    errorNotFound: 'ไม่พบโค้ดนี้ในระบบ ลองตรวจสอบโค้ดบนการ์ดอีกครั้ง',
    errorUsed: 'โค้ดนี้ถูกใช้ไปแล้ว',
    errorExpired: 'โค้ดนี้หมดอายุแล้ว',
    errorCapHit: 'งบประมาณของวันนี้เต็มแล้ว — โค้ดนี้ยังใช้ได้พรุ่งนี้',
    errorGeneric: 'แลกโค้ดไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
  },
  quota: {
    title: 'เปิดใช้งานระบบหักเงินอัตโนมัติเมื่อโควต้าฟรีหมด',
    description:
      'เมื่อโควต้ารายสัปดาห์ของโมเดลโปรโมชั่นฟรีหมดลง เลือกได้ว่าจะให้ระบบทำอย่างไรต่อ: หักเงินจาก Wallet โดยอัตโนมัติเพื่อให้ใช้งานต่อได้ไม่สะดุด หรือบล็อกการเข้าถึงโมเดลนั้นชั่วคราวจนกว่าจะรีเซ็ตในวันจันทร์ถัดไป',
    enabledLabel: 'หักเงินจาก Wallet อัตโนมัติเมื่อโควต้าฟรีหมด',
    disabledLabel: 'บล็อกการใช้งานจนกว่าโควต้ารายสัปดาห์จะรีเซ็ต',
    saving: 'กำลังบันทึก…',
    restricted: (isB2B) =>
      isB2B ? 'มีเพียงแอดมินและเจ้าขององค์กรเท่านั้นที่เปลี่ยนการตั้งค่านี้ได้' : 'มีเพียงแอดมินและเจ้าของบัญชีเท่านั้นที่เปลี่ยนการตั้งค่านี้ได้',
    saveError: 'ไม่สามารถบันทึกการตั้งค่านี้ได้',
    loadError: 'ไม่สามารถโหลดการตั้งค่านี้ได้',
  },
  firstTopUpPrompt: {
    title: 'ยินดีด้วย! เติมเงินสำเร็จแล้ว',
    body: 'หากโมเดลโปรโมชั่นฟรีใช้โควต้ารายสัปดาห์หมด คุณต้องการให้ระบบหักเงินจาก Wallet นี้โดยอัตโนมัติเพื่อให้ใช้งานต่อได้ไม่สะดุดหรือไม่ เปลี่ยนการตั้งค่านี้ได้ทุกเมื่อที่หน้ากระเป๋าเครดิต',
    accept: 'ใช่ หักเงินอัตโนมัติเมื่อโควต้าหมด',
    decline: 'ยังไม่ตอนนี้ — ใช้แค่โควต้าฟรีก่อน',
  },
  orgSwitcher: {
    label: 'บัญชี',
    optionLabel: (org, role) => `${org} (${role})`,
    switchError: 'สลับไม่สำเร็จ ลองใหม่อีกครั้ง',
  },
  orgChooser: {
    title: 'เลือกบัญชีที่จะใช้งาน',
    body: 'บัญชีนี้เป็นสมาชิกมากกว่าหนึ่งบัญชี เลือกบัญชีที่จะใช้งานตอนนี้ — สลับได้ตลอดจากแถบด้านบน',
    defaultBadge: 'ค่าเริ่มต้น',
  },
  promoCreditWelcome: {
    title: 'ยินดีด้วย!',
    body: (amount) => `คุณได้รับเครดิต ฿${amount} จากการแลกโค้ดโปรโมชั่น — เพิ่มเข้ายอดเครดิตให้แล้ว`,
    close: 'รับทราบ ขอบคุณ',
  },
  playground: {
    subtitle: 'ยิง request จริงด้วย key จริง แล้วดูว่าได้ผลลัพธ์อะไรกลับมา',
    unavailableTitle: 'ใช้งานไม่ได้ในระบบนี้',
    unavailableBody: 'หน้าทดลองใช้งานเปิดให้ใช้เฉพาะ dev และ local เท่านั้น ไม่ได้รวมอยู่ใน production build',
    gatewayUrlLabel: 'Gateway URL',
    apiKeyLabel: 'API key',
    apiKeyPlaceholder: 'llmr_sk_…',
    showKey: 'แสดง',
    hideKey: 'ซ่อน',
    modelLabel: 'ชื่อ Model (alias)',
    modelPlaceholder: 'เลือกโมเดล',
    modelSearchHint: 'พิมพ์เพื่อค้นหา',
    modelNoResults: 'ไม่พบโมเดลที่ตรงกับคำค้นหา',
    testConnection: 'ทดสอบการเชื่อมต่อ',
    testing: 'กำลังทดสอบ…',
    statusNotTested: 'ยังไม่ได้ทดสอบ',
    statusConnected: 'เชื่อมต่อแล้ว',
    statusUnreachable: 'ติดต่อไม่ได้',
    emptyTitle: 'ยังไม่มีข้อความ',
    emptyBodyReady: 'เลือก model alias แล้วพิมพ์ข้อความด้านล่าง ทุกคำตอบจะโชว์ alias ที่ระบบเลือกใช้จริง',
    emptyBodyWithFree: 'ยังไม่มีเครดิตในกระเป๋า — เลือกโมเดลป้าย Free เพื่อทดลองได้โดยไม่ต้องเติมเงิน หรือไปเติมเครดิตเพื่อใช้โมเดลอื่น',
    emptyBodyNeedCredit: 'ยังไม่มีเครดิต และตอนนี้ยังไม่มีโมเดลฟรีให้ทดลอง {topUpLink} เพื่อเริ่มทดสอบ',
    emptyBodyNeedCreditMember: (isB2B) =>
      isB2B
        ? 'องค์กรยังไม่มีเครดิต และตอนนี้ยังไม่มีโมเดลฟรีให้ทดลอง แจ้งแอดมินหรือเจ้าขององค์กรให้เติมเครดิต'
        : 'บัญชียังไม่มีเครดิต และตอนนี้ยังไม่มีโมเดลฟรีให้ทดลอง แจ้งแอดมินหรือเจ้าของบัญชีให้เติมเครดิต',
    messagePlaceholder: 'พิมพ์ข้อความ… (Enter เพื่อส่ง, Shift+Enter ขึ้นบรรทัดใหม่)',
    send: 'ส่งข้อความ',
    clearConversation: 'ล้างบทสนทนา',
    messagesSentCount: (n) => `จำนวนข้อความ: ${n}`,
    viewRawResponse: 'ดู response แบบดิบ',
    hideRawResponse: 'ซ่อน response แบบดิบ',
    needKeyAlert: 'ใส่ API key ก่อน',
    needModelAlert: 'เลือกโมเดลก่อน เช่น gpt-5-mini',
    needAlertTitle: 'ก่อนส่งข้อความ',
    networkErrorBody: (url) => `ต่อ ${url} ไม่ได้ — gateway รันอยู่ไหม และเบราว์เซอร์นี้เข้าถึงได้จริงไหม?`,
    costNote: 'ค่าใช้จ่ายไม่แสดงในผลลัพธ์นี้ โปรดตรวจสอบที่{usageLink}',
    costNoteUsageLink: 'หน้าการใช้งาน',
    apiKeyOptionalHint: 'เว้นว่างไว้เพื่อใช้เครดิตของบัญชีที่ล็อกอินอยู่ หรือวาง API key เองเพื่อทดสอบคีย์อื่น',
    sessionError: 'โหลดเครดิตของบัญชีไม่สำเร็จ วาง API key เองเพื่อใช้งานต่อได้',
    configHeading: 'การเชื่อมต่อ',
    chatHeading: 'บทสนทนา',
    nudgeAdminCta: (isB2B) => (isB2B ? 'แจ้งเตือนแอดมิน/เจ้าขององค์กร' : 'แจ้งเตือนแอดมิน/เจ้าของบัญชี'),
    nudgeAdminTitle: (isB2B) => (isB2B ? 'แจ้งเตือนแอดมิน/เจ้าขององค์กร' : 'แจ้งเตือนแอดมิน/เจ้าของบัญชี'),
    freeBadge: 'Free',
    creditBannerWithFree:
      'ยังไม่มีเครดิตในกระเป๋า จึงยังใช้โมเดลแบบเสียเงินไม่ได้ เลือกโมเดลป้าย Free เพื่อทดลองได้ทันที หรือไปเติมเครดิตที่กระเป๋าเครดิต',
    creditBannerWithFreeSpent:
      'ยอดเครดิตของคุณหมดแล้ว จึงยังใช้โมเดลแบบเสียเงินไม่ได้ เลือกโมเดลป้าย Free เพื่อทดลองได้ทันที หรือไปเติมเครดิตที่กระเป๋าเครดิต',
    creditBannerNoFree: 'ยังไม่มีเครดิต และตอนนี้ยังไม่มีโมเดลฟรีให้ทดลอง กรุณาไปเติมเครดิตก่อน จึงจะเริ่มทดสอบได้',
    creditBannerNoFreeSpent:
      'ยอดเครดิตของคุณหมดแล้ว และตอนนี้ยังไม่มีโมเดลฟรีให้ทดลอง กรุณาไปเติมเครดิตก่อน จึงจะทดสอบต่อได้',
    goTopUpCta: 'เติมเครดิต',
    creditErrorBody: 'ยอดเครดิตไม่พอสำหรับการทดสอบนี้ กรุณาไปเติมเครดิต',
    creditErrorFreeHint: 'หรือเลือกโมเดลป้าย Free เพื่อทดลองได้โดยไม่ต้องเติมเงิน',
  },
  team: {
    subtitle: (isB2B) => (isB2B ? 'ใครเข้าสู่ระบบองค์กรนี้ได้บ้าง และทำอะไรได้บ้าง' : 'ใครเข้าสู่ระบบบัญชีนี้ได้บ้าง และทำอะไรได้บ้าง'),
    loadError: 'ไม่สามารถโหลดข้อมูลทีมได้',
    limitsOwnerAdmin: (used, max) => `ผู้ดูแล ${used} จาก ${max} คน`,
    limitsOwnerAdminHint: 'ผู้ดูแลประกอบด้วยเจ้าของทีมและผู้ดูแลระบบ',
    limitsMember: (used, max) => `สมาชิก ${used} จาก ${max} คน`,
    limitsMemberUnlimited: (used) => `สมาชิก ${used} คน (ไม่จำกัด)`,
    inviteHeading: 'เพิ่มสมาชิก',
    emailLabel: 'อีเมล',
    emailPlaceholder: 'colleague@company.com',
    roleLabel: 'บทบาท',
    role: {
      owner: 'Owner',
      admin: 'ผู้ดูแลระบบ',
      member: 'สมาชิก',
    },
    expiresInLabel: 'หมดอายุใน',
    expiresInOptions: {
      '1': '1 วัน',
      '3': '3 วัน',
      '7': '7 วัน',
      '30': '30 วัน',
      '365': '1 ปี',
    },
    inviting: 'กำลังส่งคำเชิญ…',
    inviteButton: 'ส่งคำเชิญ',
    inviteError: 'ไม่สามารถส่งคำเชิญได้',
    inviteSent: (email) => `ส่งคำเชิญไปที่ ${email} เรียบร้อยแล้ว`,
    inviteErrorAlreadyMember: (isB2B) => (isB2B ? 'บัญชีนี้เป็นสมาชิกขององค์กรอยู่แล้ว' : 'บัญชีนี้อยู่ในทีมของคุณอยู่แล้ว'),
    inviteErrorAlreadyPending: 'อีเมลนี้มีคำเชิญที่รอตอบรับอยู่แล้ว หากต้องการส่งใหม่ ให้กด "ส่งอีกครั้ง" ในตารางคำเชิญที่รอตอบรับด้านล่าง',
    rosterHeading: 'สมาชิก',
    colName: 'ชื่อ',
    colEmail: 'อีเมล',
    colRole: 'บทบาท',
    colJoined: 'เข้าร่วมเมื่อ',
    actionsAria: 'การดำเนินการ',
    you: 'คุณ',
    remove: 'ลบ',
    removeTitle: 'ลบสมาชิกคนนี้ใช่หรือไม่?',
    removeBody: (name, isB2B) =>
      isB2B
        ? `${name} จะสูญเสียสิทธิ์เข้าถึงองค์กรนี้ทันที — ทั้ง key, การใช้งาน และกระเป๋าเครดิต`
        : `${name} จะสูญเสียสิทธิ์เข้าถึงทีมของคุณทันที — ทั้ง key, การใช้งาน และกระเป๋าเครดิต`,
    removeConfirmLabel: 'ลบสมาชิก',
    removeError: 'ไม่สามารถลบสมาชิกคนนี้ได้',
    emptyTitle: 'ตอนนี้มีแค่คุณคนเดียว',
    emptyBody: (isB2B) =>
      isB2B ? 'เพิ่มเพื่อนร่วมงานด้านบนเพื่อให้เข้าถึงองค์กรนี้ได้' : 'เพิ่มเพื่อนร่วมงานด้านบนเพื่อให้เข้าถึงทีมของคุณได้',
    pendingInvitesHeading: 'คำเชิญที่รอตอบรับ',
    pendingInvitesEmpty: 'ไม่มีคำเชิญที่รอตอบรับ',
    colExpires: 'หมดอายุ',
    resend: 'ส่งอีกครั้ง',
    resending: 'กำลังส่ง…',
    resendError: 'ไม่สามารถส่งคำเชิญนี้อีกครั้งได้',
    cancelInvite: 'ยกเลิก',
    cancelInviteTitle: 'ยกเลิกคำเชิญนี้ใช่หรือไม่?',
    cancelInviteBody: (email) => `${email} จะใช้ลิงก์คำเชิญนี้เข้าร่วมทีมไม่ได้อีกต่อไป`,
    cancelInviteConfirmLabel: 'ยกเลิกคำเชิญ',
    cancelInviteError: 'ไม่สามารถยกเลิกคำเชิญนี้ได้',
    invitesLoadError: 'ไม่สามารถโหลดคำเชิญที่รอตอบรับได้',
    editInvite: 'แก้ไข',
    editInviteTitle: (email) => `แก้ไขคำเชิญของ ${email}`,
    editInviteSave: 'บันทึกการแก้ไข',
    editInviteError: 'ไม่สามารถแก้ไขคำเชิญนี้ได้',
    editKeepExpiry: 'ไม่เปลี่ยนแปลง',
    historyHeading: 'ประวัติคำเชิญ',
    historySubtitle: 'คำเชิญทั้งหมดที่จบสถานะแล้ว ไม่ว่าจะหมดอายุ ยกเลิก หรือตอบรับสำเร็จ',
    historyEmpty: 'ยังไม่มีประวัติคำเชิญ',
    historyLoadError: 'ไม่สามารถโหลดประวัติคำเชิญได้',
    colInvitedBy: 'ผู้เชิญ',
    colStatus: 'สถานะ',
    colOccurredAt: 'วันที่',
    historyStatus: {
      accepted: 'ตอบรับแล้ว',
      revoked: 'ยกเลิกแล้ว',
      expired: 'หมดอายุ',
    },
    historyInvitedByUnknown: 'บัญชีถูกลบ',
    budgets: {
      heading: 'ลิมิตวงเงิน',
      subtitle: 'กำหนดวงเงินสูงสุดต่อวันหรือต่อเดือนของทั้งองค์กร หรือกำหนดแยกเฉพาะบุคคลได้',
      warning:
        'ลิมิตนี้อ้างอิงจากผู้สร้าง key ไม่ใช่คนที่ใช้งานจริง ถ้า key ถูกแชร์กันในทีม ลิมิตอาจไม่แม่นยำและหลีกเลี่ยงได้ง่าย — เพื่อความแม่นยำ แนะนำให้แต่ละคนมี key เป็นของตัวเอง',
      loadError: 'ไม่สามารถโหลดลิมิตวงเงินได้',
      periodLabel: { daily: 'ค่า default รายวัน', monthly: 'ค่า default รายเดือน' },
      periodName: { daily: 'รายวัน', monthly: 'รายเดือน' },
      notSet: 'ยังไม่ได้กำหนด',
      setButton: 'กำหนด',
      editButton: 'แก้ไข',
      removeButton: 'ลบ',
      removeDefaultTitle: 'ลบค่า default นี้ใช่หรือไม่?',
      removeDefaultBody: (period) =>
        `สมาชิกที่มีลิมิตของตัวเองอยู่แล้วจะไม่ได้รับผลกระทบ ส่วนคนที่ไม่มีลิมิตของตัวเองจะกลับไปไม่มีลิมิต${period}`,
      removeOverrideTitle: 'ลบลิมิตนี้ใช่หรือไม่?',
      removeOverrideBody: (name, period) =>
        `${name} จะกลับไปใช้ค่า default ${period} ขององค์กร หรือไม่มีลิมิตเลยถ้าองค์กรไม่ได้กำหนดไว้`,
      removeConfirmLabel: 'ลบ',
      removeError: 'ไม่สามารถลบลิมิตนี้ได้',
      overridesHeading: 'ลิมิตเฉพาะบุคคล',
      overridesEmpty: 'ยังไม่มีใครถูกกำหนดลิมิตเฉพาะบุคคล — ทุกคนใช้ค่า default ขององค์กรด้านบน',
      addOverrideButton: 'กำหนดลิมิตให้สมาชิก',
      colMember: 'สมาชิก',
      colPeriod: 'คาบเวลา',
      colLimit: 'ลิมิต',
      actionsAria: 'การดำเนินการ',
      memberLabel: 'สมาชิก',
      memberPlaceholder: 'เลือกสมาชิก',
      periodSelectLabel: 'คาบเวลา',
      amountLabel: 'ลิมิต (บาท)',
      amountPlaceholder: 'เช่น 500.00',
      modalSetDefaultTitle: (period) => `กำหนดค่า default ${period}`,
      modalEditOverrideTitle: (name) => `ลิมิตของ ${name}`,
      modalAddOverrideTitle: 'กำหนดลิมิตให้สมาชิก',
      save: 'บันทึก',
      saving: 'กำลังบันทึก…',
      saveError: 'ไม่สามารถบันทึกลิมิตนี้ได้',
    },
  },
  reports: {
    subtitle: 'สร้างรายงานบนหน้าจอ หรือส่งออกเป็น Excel หรือ PDF',
    reportLabel: 'รายงาน',
    fromLabel: 'วันที่เริ่มต้น',
    toLabel: 'วันที่สิ้นสุด',
    run: 'สร้างรายงาน',
    running: 'กำลังสร้างรายงาน…',
    exportExcel: 'ส่งออก Excel',
    exportPdf: 'ส่งออก PDF',
    exporting: 'กำลังสร้างไฟล์…',
    emptyTitle: 'ไม่มีข้อมูล',
    emptyBody: 'ไม่พบข้อมูลในช่วงเวลาที่เลือก ลองขยายช่วงวันที่',
    loadError: 'ไม่สามารถสร้างรายงานได้ กรุณาลองใหม่อีกครั้ง',
    exportError: 'ไม่สามารถสร้างไฟล์ได้',
    renderUnavailable: 'ระบบสร้าง PDF ไม่พร้อมใช้งานชั่วคราว กรุณาลองใหม่อีกครั้ง หรือส่งออกเป็น Excel',
    downloadReady: (name) => `${name} กำลังดาวน์โหลด`,
    download: 'ดาวน์โหลดอีกครั้ง',
    rowCount: (n) => `${n.toLocaleString()} รายการ`,
    generatedAt: (when) => `สร้างเมื่อ ${when}`,
    linkExpires: (when) => `ลิงก์หมดอายุ ${when}`,
    noReports: 'ไม่มีรายงานสำหรับบัญชีของคุณ',
    summaryAria: 'สรุปยอดรวมในช่วงเวลาที่เลือก',
    summaryRequests: 'คำขอทั้งหมด',
    summaryInputTokens: 'โทเคนขาเข้า',
    summaryOutputTokens: 'โทเคนขาออก',
    summaryTotalTokens: 'โทเคนรวม',
    summaryTotalCost: 'ค่าใช้จ่ายรวม',
    ledgerKind: {
      topup: 'เติมเงิน',
      usage: 'การใช้งาน',
      adjustment: 'ปรับยอดเครดิต',
      refund: 'คืนเงิน',
      reversal: 'ยกเลิกรายการ',
    },
    usageStatus: {
      completed: 'สำเร็จ',
      interrupted: 'ถูกขัดจังหวะ',
      failed: 'ล้มเหลว',
    },
    invoiceStatus: {
      draft: 'ฉบับร่าง',
      issued: 'ออกแล้ว',
      void: 'ยกเลิก',
    },
    invoiceDocType: {
      abbreviated: 'อย่างย่อ',
      full: 'เต็มรูปแบบ',
    },
    reconciliationStatus: {
      ok: 'ปกติ',
      drift_detected: 'พบผลต่าง',
      failed: 'ล้มเหลว',
    },
  },

  usage: {
    subtitle: 'ยอดใช้จ่ายตามช่วงเวลา แยกตามวัน โมเดล หรือคีย์',
    loadErrorSummary: 'ไม่สามารถโหลดสรุปการใช้งานได้',
    fromLabel: 'วันที่เริ่มต้น',
    toLabel: 'วันที่สิ้นสุด',
    groupByLabel: 'จัดกลุ่มแบบ',
    groupDay: 'รายวัน',
    groupModel: 'ตามโมเดล',
    groupKey: 'ตามคีย์ API',
    /** Noun form for table column headers and the “รายละเอียดตาม…” heading. */
    groupAxisDay: 'วัน',
    groupAxisModel: 'โมเดล',
    groupAxisKey: 'คีย์ API',
    totalSpend: 'ยอดใช้จ่ายรวม',
    rangeNote: (from, to, group) => `${from} ถึง ${to} จัดกลุ่มแบบ${group}`,
    breakdownBy: (group) => `รายละเอียดตาม${group}`,
    emptyTitle: 'ไม่มีการใช้งานในช่วงเวลานี้',
    emptyBody: 'ลองขยายช่วงวันที่',
    showingTop: (n, total) => `แสดง ${n} อันดับแรกจากทั้งหมด ${total} — ดูทุกแถวได้ในตารางด้านล่าง`,
    keyNameNone: 'ไม่มีชื่อ',
    colRequests: 'คำขอ',
    colSuccess: 'สำเร็จ',
    colFailed: 'ล้มเหลว',
    colInputTok: 'อินพุต (token)',
    colCacheReadTok: 'แคชอ่าน (token)',
    colOutputTok: 'เอาต์พุต (token)',
    colTotalTok: 'รวม token*',
    colCost: 'ค่าใช้จ่าย (บาท)',
    quotaFootnote: '* รวม input + แคชอ่าน + เอาต์พุต ← ตรงกับที่นับเข้าโควตารายสัปดาห์',
    freeBadge: 'ฟรี',
    freeBadgeTitle: 'โมเดลโปรโมชั่นฟรี — ไม่ถูกคิดเงินสำหรับคำขอนี้',
    savedCost: (amount) => `ประหยัด ฿${amount}`,
    allRequests: 'คำขอทั้งหมด',
    loadErrorEvents: 'ไม่สามารถโหลดรายการการใช้งานได้',
    colWhen: 'เวลา',
    colModel: 'โมเดล',
    colProvider: 'ผู้ให้บริการ',
    colRegion: 'Region',
    colStatus: 'สถานะ',
    colIn: 'อินพุต',
    colOut: 'เอาต์พุต',
    emptyRange: 'ไม่มีคำขอในช่วงเวลานี้',
    statusFilterLabel: 'สถานะ',
    statusAll: 'ทั้งหมด',
    statusSuccess: 'สำเร็จ',
    statusFailed: 'ล้มเหลว',
    statusInterrupted: 'ถูกขัดจังหวะ',
    requesterLabel: 'ผู้ใช้งาน',
    requesterAll: 'ผู้ใช้งานทั้งหมด',
    requesterSearchHint: 'ค้นหาด้วยชื่อหรืออีเมล',
    requesterNoResults: 'ไม่พบสมาชิกที่ตรงกับคำค้นหา',
    requesterNone: 'ไม่ระบุ',
    colRequester: 'ผู้ส่งคำขอ',
    requesterModelsTitle: (name) => `โมเดลที่ ${name} ใช้`,
    requesterBadge: (name) => name,
    quotaCardTitle: 'โควตาโมเดลฟรีสัปดาห์นี้',
    quotaResetsAt: (date: string) => `รีเซ็ต ${date}`,
    quotaTokensLabel: 'Token',
    quotaRequestsLabel: 'คำขอ',
    quotaExceeded: 'โควตาหมดแล้ว',
  },
  onboarding: {
    dashboardArea: 'แดชบอร์ดของคุณ',
    keysArea: 'API keys',
    usageArea: 'การใช้งาน',
    modelsArea: 'รายการโมเดล',
    topupArea: 'การเติมเงิน',
    teamArea: 'ทีม',
    title: (area) => `${area}จะใช้งานได้เมื่อบัญชีของคุณตั้งค่าเสร็จสมบูรณ์`,
    body: 'กระเป๋าเครดิต, API keys และการใช้งาน ล้วนถูกจัดกลุ่มภายใต้บัญชีของคุณ หากคุณเพิ่งสมัครเมื่อสักครู่ อาจต้องรอสักครู่หนึ่ง หรือหากมีผู้ดูแลระบบที่จะเพิ่มคุณเข้าทีมที่มีอยู่แล้ว ให้ตรวจสอบอีกครั้งหลังจากนั้น',
    note: 'หากดูเหมือนมีบางอย่างผิดพลาด กรุณาติดต่อฝ่ายสนับสนุนพร้อมแจ้งอีเมลบัญชีของคุณ',
  },
  profile: {
    title: 'ตั้งค่าโปรไฟล์',
    subtitle: 'ข้อมูลส่วนตัวและรหัสผ่านของบัญชีนี้',
    changePhoto: 'เปลี่ยนรูป',
    uploadPhoto: 'อัปโหลดรูป',
    deletePhoto: 'ลบรูป',
    personalHeading: 'ข้อมูลส่วนตัว',
    personalDescription: 'ชื่อและ username แสดงในพอร์ทัล อีเมลเปลี่ยนไม่ได้',
    nameLabel: 'ชื่อ',
    usernameLabel: 'Username',
    usernamePlaceholder: 'ตั้ง username',
    usernameHint: 'ตั้งได้ครั้งเดียว หลังจากบันทึกแล้วจะเปลี่ยนไม่ได้',
    usernameLockedHint: 'ตั้ง username แล้ว ไม่สามารถเปลี่ยนได้',
    emailLabel: 'อีเมล',
    emailHint: 'เปลี่ยนอีเมลไม่ได้',
    dobLabel: 'วันเดือนปีเกิด',
    dobDayLabel: 'วัน',
    dobMonthLabel: 'เดือน',
    dobYearLabel: 'ปี',
    phoneLabel: 'เบอร์โทร',
    phoneCountryLabel: 'รหัสประเทศ',
    unlockField: 'แก้ไข',
    linkedProvidersLabel: 'เชื่อมต่อผ่าน',
    linkedProviderBadge: (provider: string) =>
      provider === 'google' ? 'Google'
      : provider === 'microsoft' ? 'Microsoft'
      : provider,
    socialOnlyPasswordNote:
      'บัญชีของคุณเข้าสู่ระบบผ่าน Social login จึงไม่มีรหัสผ่านในระบบ',
    securityHeading: 'รหัสผ่าน',
    passwordDescription:
      'ใส่รหัสผ่านเดิม แล้วตั้งรหัสใหม่ (อย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ)',
    changePassword: 'เปลี่ยนรหัสผ่าน',
    passwordLockedHint: 'รหัสผ่านจะถูกซ่อนไว้ จนกว่าคุณจะเลือกเปลี่ยน',
    currentPasswordLabel: 'รหัสผ่านเดิม',
    newPasswordLabel: 'รหัสผ่านใหม่',
    confirmPasswordLabel: 'ยืนยันรหัสผ่านใหม่',
    showPassword: 'แสดงรหัสผ่าน',
    hidePassword: 'ซ่อนรหัสผ่าน',
    save: 'บันทึก',
    saved: 'บันทึกโปรไฟล์แล้ว',
    passwordSaved: 'อัปเดตรหัสผ่านแล้ว',
    phoneVerifyBadgeVerified: 'ยืนยันแล้ว',
    phoneVerifyBadgeUnverified: 'ยังไม่ยืนยัน',
    phoneVerifyStartButton: 'ส่งรหัสยืนยัน',
    phoneVerifySending: 'กำลังส่ง…',
    phoneVerifyWhyNote: 'ยืนยันเบอร์เพื่อใช้ SMS เป็นขั้นตอนที่สองตอนเข้าสู่ระบบ',
    phoneVerifyModalTitle: 'ยืนยันเบอร์โทรศัพท์',
    phoneVerifySentTo: 'ส่งรหัส 6 หลักไปที่ {phone} แล้ว',
    phoneVerifyCodeLabel: 'กรอกรหัส 6 หลัก',
    phoneVerifyExpiresIn: 'รหัสหมดอายุใน {time}',
    phoneVerifyExpired: 'รหัสหมดอายุแล้ว ปิดหน้าต่างนี้แล้วขอรหัสใหม่',
    phoneVerifyConfirmButton: 'ยืนยัน',
    phoneVerifyConfirming: 'กำลังยืนยัน…',
    phoneVerifyDone: 'ยืนยันเบอร์โทรศัพท์แล้ว',
    errPhoneVerifyNoPhone: 'กรอกและบันทึกเบอร์โทรศัพท์ก่อน แล้วจึงยืนยัน',
    errPhoneVerifyAlreadyVerified: 'เบอร์นี้ยืนยันแล้ว',
    errPhoneVerifyNotMobile: 'ต้องเป็นเบอร์มือถือที่รับ SMS ได้',
    errPhoneVerifyCode: 'รหัสไม่ถูกต้องหรือหมดอายุแล้ว ลองใหม่หรือขอรหัสใหม่',
    errPhoneVerifyThrottled: 'ขอรหัสถี่เกินไป กรุณารอสักครู่แล้วลองใหม่',
    errPhoneVerifyUnavailable: 'ตอนนี้ส่งรหัสไม่ได้ กรุณาลองใหม่อีกสักครู่',
    errPhoneVerifyGeneric: 'ยืนยันเบอร์ไม่สำเร็จ กรุณาลองใหม่',
    twoFactorHeading: 'การยืนยันตัวตนสองขั้นตอน',
    twoFactorDescription: 'เพิ่มขั้นตอนที่สองตอนเข้าสู่ระบบ',
    twoFactorEnforcedNote: 'องค์กรของคุณกำหนดให้ต้องตั้งค่านี้',
    methodName: (method) => {
      switch (method) {
        case 'totp':
          return 'แอป authenticator';
        case 'sms':
          return 'SMS';
        case 'email_otp':
          return 'อีเมล';
        default:
          return method;
      }
    },
    twoFactorEnrolledStatusFor: (method) => `ตั้งค่า${method}แล้ว`,
    twoFactorNotEnrolledStatusFor: (method) => `ยังไม่ได้ตั้งค่า${method}`,
    twoFactorSetupButtonFor: (method) => `ตั้งค่า${method}`,
    twoFactorSmsConfirmTitle: 'ตั้งค่า SMS',
    twoFactorSmsConfirmBody: (phone) =>
      `ระบบจะส่งรหัส OTP ไปยังเบอร์ ${phone} กดยืนยันเพื่อดำเนินการต่อ`,
    twoFactorModalTitleFor: (method) => `ตั้งค่า${method}`,
    twoFactorScanLede: 'สแกน QR code นี้ด้วยแอป authenticator หรือกรอกรหัสด้านล่างด้วยตนเอง',
    twoFactorSecretLabel: 'รหัสสำหรับกรอกด้วยตนเอง',
    twoFactorCodeLabel: 'กรอกรหัส 6 หลักจากแอป',
    twoFactorOTPLede: (destinationMasked) => `เราส่งรหัสไปที่ ${destinationMasked} แล้ว`,
    twoFactorOTPCodeLabel: 'กรอกรหัส 6 หลัก',
    twoFactorSendingCode: 'กำลังส่งรหัส…',
    twoFactorResendCode: 'ส่งรหัสอีกครั้ง',
    twoFactorResendSent: 'ส่งอีกครั้งแล้ว',
    errTwoFactorSendFailed: 'ไม่สามารถส่งรหัสได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
    twoFactorConfirmButton: 'ยืนยัน',
    twoFactorConfirming: 'กำลังยืนยัน…',
    twoFactorRecoveryModalTitle: 'บันทึก recovery code ไว้',
    twoFactorRecoveryWarning: 'รหัสเหล่านี้จะแสดงเพียงครั้งเดียวเท่านั้น',
    twoFactorRecoveryWarningBody:
      'หากทำแอป authenticator หาย recovery code คือทางเดียวที่จะกลับเข้าบัญชีได้ — บันทึกไว้ในที่ปลอดภัยก่อนไปต่อ',
    twoFactorRecoveryCopyAll: 'คัดลอกทั้งหมด',
    twoFactorRecoveryCopied: 'คัดลอกแล้ว',
    twoFactorRecoveryAckLabel: 'ฉันได้บันทึก recovery code เหล่านี้ไว้ในที่ปลอดภัยแล้ว',
    twoFactorRecoveryDone: 'เสร็จสิ้น',
    twoFactorDisableButtonFor: (method) => `ลบ${method}`,
    twoFactorDisableModalTitleFor: (method) => `ลบ${method}`,
    twoFactorDisableWarning: 'ต้องยืนยันตัวตนอีกครั้งก่อนลบการตั้งค่านี้',
    twoFactorDisableTotpLede: 'กรอกรหัส 6 หลักจากแอป authenticator ปัจจุบันเพื่อยืนยัน',
    twoFactorDisableOtpLede: (destinationMasked) => `เราส่งรหัสยืนยันไปที่ ${destinationMasked} แล้ว`,
    twoFactorDisableCodeLabel: 'กรอกรหัสยืนยัน',
    twoFactorDisableConfirmButton: 'ยืนยันการลบ',
    twoFactorDisableConfirming: 'กำลังลบ…',
    twoFactorDisableDone: 'ลบการตั้งค่าเรียบร้อยแล้ว',
    errTwoFactorDisableCode: 'รหัสไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง',
    errTwoFactorDisableSendFailed: 'ไม่สามารถส่งรหัสได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
    errTwoFactorDisableGeneric: 'ไม่สามารถลบการตั้งค่านี้ได้ในขณะนี้',
    securityHistoryHeading: 'ประวัติความปลอดภัย',
    securityHistoryDescription: 'เหตุการณ์ล่าสุดที่เกี่ยวข้องกับความปลอดภัยของบัญชีคุณ',
    securityHistoryFilterLabel: 'กรองตาม',
    securityHistoryFilterAll: 'ทั้งหมด',
    securityHistoryEmpty: 'ยังไม่มีประวัติ',
    securityHistoryCategoryTitle: (category) => {
      switch (category) {
        case 'two_factor':
          return 'การยืนยันตัวตนสองขั้นตอน';
        case 'password':
          return 'รหัสผ่าน';
        case 'recovery_code':
          return 'Recovery code';
        case 'session':
          return 'การเข้าสู่ระบบ';
        default:
          return category;
      }
    },
    securityHistoryEventLabel: (action) => {
      switch (action) {
        case 'auth.totp.enrolled':
          return 'ตั้งค่าแอป authenticator';
        case 'auth.sms.enrolled':
          return 'ตั้งค่า SMS';
        case 'auth.email_otp.enrolled':
          return 'ตั้งค่ายืนยันทางอีเมล';
        case 'auth.totp.disabled':
          return 'ลบแอป authenticator';
        case 'auth.sms.disabled':
          return 'ลบ SMS';
        case 'auth.email_otp.disabled':
          return 'ลบการยืนยันทางอีเมล';
        case 'auth.password_change.completed':
          return 'เปลี่ยนรหัสผ่าน';
        case 'auth.recovery_code.used':
          return 'ใช้ recovery code';
        case 'auth.session.created':
          return 'เข้าสู่ระบบ';
        default:
          return action;
      }
    },
    errName: 'กรุณากรอกชื่อ',
    errUsername: 'ใช้ 3–32 ตัวอักษร ตัวเลข จุด ขีดล่าง หรือขีดกลาง',
    errUsernameTaken: 'ชื่อผู้ใช้นี้ถูกใช้แล้ว',
    errUsernameImmutable: 'ตั้งชื่อผู้ใช้ได้เพียงครั้งเดียว',
    errDob: 'กรอกวันเกิดให้ถูกต้อง',
    errPhone: 'กรอกเบอร์โทรให้ถูกต้อง หรือเว้นว่างไว้',
    errPhoneTaken: 'เบอร์โทรศัพท์นี้ถูกใช้แล้ว',
    errAvatarType: 'เลือกไฟล์รูปภาพ (JPG หรือ PNG)',
    errAvatarSize: 'ขนาดรูปต้องไม่เกิน 512 KB',
    errCurrentPassword: 'กรุณากรอกรหัสผ่านเดิม',
    errNewPassword:
      'รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร และต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ',
    errPasswordSame: 'รหัสผ่านใหม่ต้องต่างจากรหัสเดิม',
    errPasswordMismatch: 'รหัสผ่านใหม่ไม่ตรงกัน',
    errWrongPassword: 'รหัสผ่านเดิมไม่ถูกต้อง',
    errNoPassword: 'บัญชีนี้ไม่มีรหัสผ่านให้เปลี่ยน',
    errPasswordRateLimit: 'ลองบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่',
    errPasswordGeneric: 'อัปเดตรหัสผ่านไม่สำเร็จ กรุณาลองใหม่',
    errProfileGeneric: 'บันทึกโปรไฟล์ไม่สำเร็จ กรุณาลองใหม่',
    errTwoFactorCode: 'รหัสไม่ถูกต้อง กรุณาลองใหม่',
    errTwoFactorAlreadyEnrolled: 'ตั้งค่าแอป authenticator ไว้แล้ว หากต้องการรีเซ็ตให้ใช้ recovery code หรือติดต่อ org admin',
    errTwoFactorGeneric: 'ตั้งค่าแอป authenticator ไม่สำเร็จ กรุณาลองใหม่',
  },

  guide: {
    eyebrow: 'ศูนย์ช่วยเหลือ',
    title: 'คู่มือการใช้งานระบบ',
    intro:
      'รวมคำอธิบายทุกเมนูในระบบไว้ที่เดียว — แต่ละหัวข้อบอกว่าหน้านั้นคืออะไร ทำหน้าที่อะไร และเริ่มใช้งานอย่างไร เลือกหัวข้อทางขวามือเพื่อข้ามไปยังส่วนที่ต้องการได้ทันที',
    hint: 'เมนูจะเรียงตามลำดับที่ปรากฏในแถบด้านซ้ายของคุณ',
    onThisPage: 'ในหน้านี้',
    fieldDoes: 'ทำอะไรได้',
    fieldStart: 'เริ่มยังไง',
    keysConnectDocsLabel: 'คู่มือการตั้งค่า Connector แบบครบวงจร →',
    tagAdminOnly: (isB2B) => (isB2B ? 'เฉพาะแอดมิน/เจ้าขององค์กร' : 'เฉพาะแอดมิน/เจ้าของบัญชี'),
    tagEnvGated: 'แสดงเฉพาะบางสภาพแวดล้อม',
    tagViaAvatarMenu: 'เปิดจากรูปโปรไฟล์มุมขวาบน',
    tagViaBell: 'เปิดจากไอคอนกระดิ่งมุมขวาบน',
    sections: {
      dashboard: {
        lede: 'หน้าแรกที่คุณเห็นทันทีหลังเข้าสู่ระบบ สรุปสถานะบัญชีทั้งหมดไว้ในมุมมองเดียว',
        does: 'แสดงยอดเครดิตคงเหลือ ฮีทแมปการใช้งานล่าสุด รายการอีเวนต์การเรียกใช้งานล่าสุด และการแจ้งเตือนเมื่อยอดเครดิตใกล้หมด',
        start: 'แวะมาเช็คยอดเงินและความเคลื่อนไหวแบบเร็ว ๆ ก่อนไปทำงานต่อ — หากยอดใกล้หมดจะมีปุ่มเติมเงินขึ้นให้ตรงนี้เลย',
      },
      keys: {
        lede: 'ศูนย์กลางจัดการกุญแจ API ที่ใช้เชื่อมต่อแอปพลิเคชันของคุณเข้ากับระบบ',
        does: 'สร้าง แก้ไข หรือเพิกถอนคีย์ และกำหนดวันหมดอายุ นโยบายเส้นทางของแต่ละคีย์จะถูกตั้งค่าโดยทีมงาน หากระบบตรวจพบกิจกรรมผิดปกติ คีย์จะถูกระงับอัตโนมัติและคุณจะได้รับการแจ้งเตือนในแอปพร้อมปุ่มเปิดใช้งานคืนทันที',
        start: 'กด "สร้างคีย์ใหม่" ตั้งชื่อให้จำง่ายและกำหนดวันหมดอายุ แล้วคัดลอกคีย์ไปใส่ในโค้ด — ระบบจะแสดงให้เห็นครั้งเดียวเท่านั้น',
      },
      usage: {
        lede: 'หน้าวิเคราะห์การใช้งานแบบละเอียด สำหรับตรวจสอบว่าใครเรียกอะไรไปเท่าไหร่',
        does: 'ดูข้อมูลแยกตามช่วงวันที่ แยกตามโมเดลหรือคีย์ สัดส่วนคำขอสำเร็จ/ล้มเหลว และรายการ event แบบแบ่งหน้า',
        start: 'เลือกช่วงวันที่และตัวกรองที่ต้องการ ใช้หาว่าโมเดลไหนถูกเรียกมากที่สุด หรือไล่หาสาเหตุของคำขอที่ล้มเหลว',
      },
      reports: {
        lede: 'หน้าสำหรับสรุปและดาวน์โหลดข้อมูลออกไปใช้ต่อนอกระบบ',
        does: 'ส่งออกรายงานการใช้งานและค่าใช้จ่ายเป็นไฟล์ (เช่น CSV) พร้อมการ์ดสรุปยอดตามช่วงเวลาที่เลือก',
        start: 'เลือกช่วงวันที่ที่ต้องการแล้วกดดาวน์โหลด เหมาะสำหรับนำไปทำบัญชีหรือส่งรายงานให้ทีม',
      },
      models: {
        lede: 'แคตตาล็อกโมเดล AI ทั้งหมดที่เปิดให้เรียกใช้ผ่านระบบนี้',
        does: 'เรียกดูโมเดลแยกตามประเภท (แชท / embedding / รูปภาพ / วิดีโอ / เสียง) พร้อมราคาต่อหน่วยของแต่ละโมเดล',
        start: 'เลือกโมเดลที่สนใจแล้วกด "Quick start" เพื่อคัดลอกโค้ดตัวอย่าง (curl / python / node) ไปเริ่มเรียกใช้ได้ทันที',
      },
      topUp: {
        lede: (isB2B) =>
          isB2B
            ? 'หน้าจัดการยอดเครดิตขององค์กร มองไม่เห็นในเมนูซ้ายหากคุณไม่ใช่แอดมิน'
            : 'หน้าจัดการยอดเครดิตของบัญชี มองไม่เห็นในเมนูซ้ายหากคุณไม่ใช่แอดมิน',
        does: 'เติมเงินผ่านบัตรเครดิตหรือ PromptPay QR แนบสลิปโอนเงินกรณีระบบอัตโนมัติมีปัญหา จัดการข้อมูลใบกำกับภาษี และดูประวัติการเติมเงินย้อนหลัง',
        start: 'กดเติมเงิน เลือกช่องทางชำระที่สะดวก แล้วยืนยันรายการ — ยอดจะอัปเดตในภาพรวมทันทีที่ชำระสำเร็จ',
      },
      team: {
        lede: (isB2B) => (isB2B ? 'หน้าจัดการสมาชิกในองค์กรของคุณ' : 'หน้าจัดการสมาชิกในทีมของคุณ'),
        does: 'ดูรายชื่อสมาชิกทั้งหมด เชิญสมาชิกใหม่ทางอีเมล ส่งคำเชิญซ้ำ หรือยกเลิกคำเชิญที่ค้างอยู่',
        start: 'กด "เชิญสมาชิก" กรอกอีเมลเพื่อนร่วมทีม ระบบจะส่งลิงก์เชิญให้ทางอีเมลโดยอัตโนมัติ',
      },
      playground: {
        lede: 'ห้องทดลองแชทกับโมเดลได้โดยตรงในเบราว์เซอร์ ไม่ต้องเขียนโค้ด',
        does: 'ทดสอบเรียกโมเดลแบบเรียลไทม์ พร้อมเห็นยอดเครดิตที่ถูกหักจริงระหว่างทดสอบ และมีปุ่ม "ขอเติมเครดิต" ขึ้นให้ทันทีหากโควตาหรือเครดิตไม่พอ',
        start: 'พิมพ์ข้อความทดสอบแล้วกดส่ง เพื่อดูผลลัพธ์และต้นทุนจริงก่อนนำไปใช้ในโค้ดของคุณ',
      },
      profile: {
        lede: 'หน้าตั้งค่าบัญชีส่วนตัว ไม่ได้อยู่ในแถบเมนูซ้าย — เข้าถึงผ่านรูปโปรไฟล์ที่มุมขวาบนแทน',
        does: 'แก้ไขชื่อ รูปโปรไฟล์ และเบอร์โทร เปลี่ยนรหัสผ่าน เปิดใช้งาน 2FA (แอปยืนยันตัวตน / SMS / อีเมล OTP) ขอรหัสสำรอง และดูประวัติความปลอดภัยของบัญชี',
        start: 'กดรูปโปรไฟล์มุมขวาบน → "แก้ไขโปรไฟล์" แนะนำให้เปิด 2FA ไว้เสมอเพื่อเพิ่มความปลอดภัยให้บัญชีของคุณ',
      },
      notifications: {
        lede: 'ประวัติการแจ้งเตือนทั้งหมดของคุณ ไม่ได้อยู่ในแถบเมนูซ้าย — เข้าถึงผ่านไอคอนกระดิ่งที่มุมขวาบนแทน',
        does: (isB2B) =>
          `ดูการแจ้งเตือนทั้งหมดที่เคยส่งถึงคุณ (คีย์ API ถูกระงับ ยอดเงินต่ำ โควต้าฟรีหมด มีคำขอเติมเงิน ถูกเพิ่มเข้า${isB2B ? 'องค์กร' : 'ทีม'}) กรองตามประเภท และไปยังหน้าที่เกี่ยวข้องหรือติดต่อฝ่ายสนับสนุนได้จากแต่ละรายการ การแจ้งเตือน "คีย์ถูกระงับ" จะมีปุ่มเปิดใช้งานคืนให้กดได้ทันทีโดยไม่ต้องออกจากหน้านี้`,
        start: 'กดไอคอนกระดิ่งมุมขวาบน แล้วเลือก "ดูการแจ้งเตือนทั้งหมด" ที่ด้านล่างของกล่องแจ้งเตือน',
      },
    },
  },
};
